import React, { useState, useEffect, useRef, useMemo, memo, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { format, parseISO, formatISO, differenceInMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  MapPin, 
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Plane,
  Building,
  Map,
  Calendar,
  DollarSign,
  Info,
  Clock,
  PlaneTakeoff,
  Utensils,
  Car,
  ArrowRight,
  Loader2,
  CreditCard,
  Check,
  User, 
  Phone, 
  Mail, 
  MapIcon, 
  X, 
  Settings,
  LogIn,
  LogOut,
  Bed,
  Eye,
  ExternalLink,
  CalendarX,
  Layers,
  FileText,
  Printer,
  Lock,
  Home,
  Menu,
  ArrowLeftCircle,
  Share2,
  Globe,
  Maximize,
  MapPinned,
  Download,
  Edit,
  FileText as FileTextIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from '@/lib/utils';
import { formatSafeDate, formatCoverDateRange } from "@/lib/date-utils";
import { mapQuotationDatesFromDb, resolveCoverDates } from "@/lib/quotation-display-dates";
import { tripService } from '@/lib/supabase/trips';
import { createGoogleMapsUrl, openInGoogleMaps } from "@/lib/google-maps-utils";
import { 
  Quotation, 
  QuotationBlock, 
  QuotationPage, 
  FlightInfo as BaseFlightInfo,
  FlightConnection,
  AccommodationInfo,
  TourInfo,
  TransportationInfo,
  RestaurantInfo,
  UnifiedItem,
  FlightEvent,
  AccommodationEvent
} from "@/components/quotation/types";
import { QuotationStickyScroll } from '@/components/ui/quotation-sticky-scroll';
import { loadGoogleMapsApi } from '@/lib/google-maps-loader';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose, DialogTrigger } from "@/components/ui/dialog";
import PageMapMobile from '@/components/ui/page-map-mobile';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from '@/lib/supabase/client';
import { quotationService } from '@/lib/supabase/quotations';
import { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { ToastAction } from "@/components/ui/toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatCurrency } from "@/lib/currency";
import AccommodationsTab from '@/components/quotation/tabs/AccommodationsTab';
import { toast } from '@/hooks/use-toast';
import { useFlightsState, Connection } from '@/hooks/useFlightsState';
import { v4 as uuidv4 } from 'uuid';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { QuotationPageNavigation } from '@/components/quotation/QuotationPageNavigation';
import { format as formatRFC3339 } from 'date-fns';
import { formatFlightDate, formatFlightTime } from '@/lib/flight-datetime-utils';
import { CustomTabView } from '@/components/quotation/CustomTab';

// Interface estendida para adicionar propriedades adicionais
interface FlightInfo extends BaseFlightInfo {
  title?: string;
  route?: string;
}

function ImageCarousel({ images, title }: { images?: string[], title?: string }) {
  if (!images || images.length === 0) return null;
  
  return (
    <Carousel className="w-full max-w-3xl mx-auto">
      <CarouselContent>
        {images.map((image, index) => (
          <CarouselItem key={index}>
            <div className="relative h-60 md:h-80 lg:h-96 w-full overflow-hidden rounded-xl">
              <img 
                src={image} 
                alt={`Imagem ${index + 1}`} 
                className="w-full h-full object-cover"
              />
              {title && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20 flex items-end p-4">
                  <h3 className="text-white text-xl md:text-2xl font-bold">{title}</h3>
                </div>
              )}
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious className="left-2" />
      <CarouselNext className="right-2" />
    </Carousel>
  );
}

function WelcomeTab({
  quotation,
  coverDateRange,
}: {
  quotation: Quotation;
  coverDateRange: string;
}) {
  // Format destination for display
  const destinationDisplay = typeof quotation.destination === 'string' 
    ? quotation.destination 
    : quotation.destination.join(', ');
    
  // Function to get client names either from the string or from the linked trip
  const formatClientNames = (clientsString: string | undefined) => {
    // If we have a linked trip, get client names from there
    if (quotation.tripId) {
      try {
        // Load trips from localStorage
        const savedTrips = localStorage.getItem('voah_trips');
        if (savedTrips) {
          const trips = JSON.parse(savedTrips);
          const linkedTrip = trips.find((trip: any) => trip.id === quotation.tripId);
          
          if (linkedTrip && linkedTrip.clientIds && linkedTrip.clientIds.length > 0) {
            // Load clients from localStorage
            const savedClients = localStorage.getItem('voah_clients');
            if (savedClients) {
              const clients = JSON.parse(savedClients);
              const tripClients = clients.filter((client: any) => 
                linkedTrip.clientIds.includes(client.id)
              );
              
              // Return full client names
              return tripClients
                .map((client: any) => client.name) // Use full name
                .join(', ');
            }
          }
        }
      } catch (error) {
        // Error silenciado
      }
    }
    
    // Fallback to the client string if no trip clients were found
    // Return exactly as stored, without any manipulation
    return clientsString || '';
  };

    return (
    <div>
      {/* Hero image para dispositivos móveis - apenas na aba Boas-vindas */}
      <div className="lg:hidden relative">
        <div className="h-56 sm:h-64 w-full overflow-hidden">
          <img 
            src={quotation.coverImages?.[0] || 'https://images.unsplash.com/photo-1506744038136-46273834b3fb'} 
            alt={quotation.title} 
            className="w-full h-full object-cover"
          />
          {/* Gradiente para melhor legibilidade do texto */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-transparent"></div>
        </div>
        
        {/* Conteúdo sobreposto à imagem */}
        <div className="absolute top-0 left-0 w-full p-4">
          <h1 className="text-xl font-bold text-white mb-1 text-left">{quotation.title}</h1>
          <div className="text-white/90 text-sm mb-1 text-left">
            <span>{formatClientNames(quotation.client)}</span>
          </div>
          <div className="text-white/90 text-sm mb-1 flex items-center text-left">
            <MapPin className="w-3 h-3 mr-1" />
            <span>{destinationDisplay}</span>
          </div>
          {coverDateRange && (
            <div className="text-white/80 text-xs flex items-center mt-1 text-left">
              <Calendar className="w-3 h-3 mr-1" />
              <span>{coverDateRange}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Conteúdo da aba */}
      {!quotation.notes ? (
        <div className="p-3 sm:p-6 lg:p-8">
        <div className="bg-gray-100 p-4 rounded-md text-center text-gray-500">
          Sem informações adicionais disponíveis.
        </div>
      </div>
      ) : (
        <div className="p-3 sm:p-6 lg:p-8">
          <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none prose-headings:text-voah-teal prose-headings:font-medium prose-p:text-gray-700 prose-strong:text-gray-800 prose-strong:font-medium prose-a:text-voah-teal prose-a:no-underline hover:prose-a:underline prose-li:text-gray-700 prose-li:marker:text-voah-teal text-left">
        <div 
          dangerouslySetInnerHTML={{ 
            __html: quotation.notes
              .replace(/style="[^"]*"/g, '')
              .replace(/style='[^']*'/g, '')
              .replace(/<p>/g, '<p class="mb-4 px-2 py-1 drop-cap-paragraph">')
              .replace(/<p class="mb-4 px-2 py-1 drop-cap-paragraph">(\s*)<\/p>/g, '')
          }} 
              className="text-[14px] sm:text-[16px] leading-loose text-left magazine-content" 
        />
      </div>
        </div>
      )}
    </div>
  );
}

function FlightsTab({ quotation }: { quotation: Quotation }) {
  // Use o novo hook para gerenciar o agrupamento de voos
  const {
    flightsByGroup,
    hasFlights,
    expandedFlights,
    collapsedSections,
    toggleSection,
    toggleFlightDetails,
    calculateTotalFlightTime,
    useCustomGroups,
    calculateWaitTime
  } = useFlightsState(quotation);

  // Formatação de data
  const formatDate = (dateStr: string) => {
    return formatFlightDate(dateStr);
  };

  // Formatação de hora
  const formatTime = (dateStr: string) => {
    return formatFlightTime(dateStr);
  };

  if (!hasFlights) {
    return (
      <div className="p-3 sm:p-6 lg:p-8">
        <div className="bg-gray-100 p-4 rounded-md text-center text-gray-500">
          Não há informações de voos disponíveis para esta cotação.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8">
      {/* Exibir descrição geral de voos se existir */}
      {quotation.flightsDescription && quotation.flightsDescription.trim() !== '' && (
        <div className="mb-6">
          <RichTextEditor 
            value={quotation.flightsDescription}
            onChange={() => {}}
            readOnly={true}
            className="border-none shadow-none bg-transparent p-0"
          />
        </div>
      )}
      
      <div className="space-y-6 sm:space-y-8">
        {Object.entries(flightsByGroup).map(([groupId, { groupTitle, groupDescription, flights }]) => (
          <div key={groupId} className="border border-gray-200 rounded-lg overflow-hidden">
            <div 
              className="bg-gray-50 px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 flex justify-between items-center cursor-pointer"
              onClick={() => toggleSection(groupId)}
            >
              <h3 className="text-lg sm:text-xl font-bold text-voah-teal">{groupTitle}</h3>
              <ChevronDown 
                className={`h-5 w-5 text-voah-teal transform transition-transform duration-200 ${
                  collapsedSections[groupId] ? '' : 'rotate-180'
                }`} 
              />
            </div>
            
            {groupDescription && !collapsedSections[groupId] && (
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <RichTextEditor 
                  value={groupDescription}
                  onChange={() => {}}
                  readOnly={true}
                  className="border-none shadow-none bg-transparent p-0 w-full"
                />
              </div>
            )}
            
            <div className={`divide-y divide-gray-200 ${collapsedSections[groupId] ? 'hidden' : ''}`}>
              {flights.map((flight) => {
                // Determine final destination and arrival time based on connections
                let finalDestination = flight.arrivalAirport;
                let finalArrivalDateTime = flight.arrivalDateTime;
                let finalArrivalTerminal = flight.arrivalTerminal;
                const hasConnections = flight.connections && flight.connections.length > 0;
                const isExpanded = expandedFlights[flight.id] || false;
                
                // Log para depuração dos dados de voo
                console.log('Dados do voo:', { 
                  id: flight.id, 
                  departureAirport: flight.departureAirport,
                  arrivalAirport: flight.arrivalAirport,
                  airline: flight.airline,
                  flightNumber: flight.flightNumber
                });
                
                // If there are connections, use the last connection's arrival info
                if (hasConnections && flight.connections.length > 0) {
                  const lastConnection = flight.connections[flight.connections.length - 1];
                  finalDestination = lastConnection.arrivalAirport;
                  finalArrivalDateTime = lastConnection.arrivalDateTime;
                  finalArrivalTerminal = lastConnection.arrivalTerminal;
                }
                
                return (
                  <div id={`flight-${flight.id}`} key={flight.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 my-4 mx-4">
                    {/* Flight header with airline info */}
                    <div className="bg-voah-teal text-white p-4">
                      <div className="flex flex-col sm:flex-row items-center justify-between">
                        <div className="w-full sm:w-auto mb-3 sm:mb-0">
                          <div className="flex items-center justify-center sm:justify-start">
                            <PlaneTakeoff className="h-6 w-6 mr-3" />
                            <h3 className="text-xl font-semibold text-center sm:text-left">
                              {flight.title || (() => {
                                // Determine final destination based on connections
                                let finalDestination = flight.arrivalAirport || '';
                                if (flight.connections && flight.connections.length > 0) {
                                  finalDestination = flight.connections[flight.connections.length - 1].arrivalAirport;
                                }
                                return `${flight.departureAirport || ''} - ${finalDestination}`;
                              })()}
                            </h3>
                          </div>
                          <p className="text-white/80 text-sm text-center sm:text-left mt-1">
                            {flight.airline || 'Companhia aérea não especificada'} {flight.flightNumber && `• ${flight.flightNumber}`}
                          </p>
                        </div>
                        <div className="text-center sm:text-right">
                          {flight.price && (
                            <div className="text-lg font-medium">
                              {flight.price.toLocaleString('pt-BR', { 
                                style: 'currency', 
                                currency: flight.currency || 'BRL' 
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Flight route visualization */}
                    <div className="p-6">
                      <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between mb-6">
                        {/* Departure information */}
                        <div className="text-center sm:text-left mb-4 sm:mb-0 sm:w-1/3">
                          <div className="text-sm text-gray-500 mb-1">Partida</div>
                          <div className="text-2xl font-bold text-voah-teal">{flight.departureAirport}</div>
                          {flight.departureDateTime && (
                            <div className="text-sm text-gray-700">
                              {formatDate(flight.departureDateTime)}
                              <span className="mx-1">•</span>
                              {formatTime(flight.departureDateTime)}
                            </div>
                          )}
                          {flight.departureTerminal && (
                            <div className="text-xs text-gray-500 mt-1">Terminal {flight.departureTerminal}</div>
                          )}
                        </div>
                    
                        {/* Flight path visualization */}
                        <div className="flex flex-col items-center justify-center py-2 mb-4 sm:mb-0 sm:w-1/3">
                          <div className="relative w-full flex items-center justify-center">
                            <div className="h-0.5 bg-gray-300 w-full absolute"></div>
                            <ArrowRight className="text-voah-teal z-10 h-6 w-6 bg-white rounded-full p-0.5 shadow-sm" />
                          </div>
                          <div className="text-sm text-gray-700 mt-2">
                            <span className="font-medium">Duração: </span>
                            {calculateTotalFlightTime(flight)}
                          </div>
                          {hasConnections && (
                            <div className="flex flex-col items-center">
                              <div className="text-xs text-voah-teal-dark">
                                {flight.connections.length} {flight.connections.length === 1 ? 'conexão' : 'conexões'}
                              </div>
                              <button 
                                onClick={() => toggleFlightDetails(flight.id)}
                                className="text-xs text-voah-teal hover:text-voah-teal-dark underline mt-1 focus:outline-none"
                              >
                                {isExpanded ? '(Ocultar Detalhes)' : '(Clique para Detalhes)'}
                              </button>
                            </div>
                          )}
                        </div>
                    
                        {/* Arrival information - show final destination */}
                        <div className="text-center sm:text-right sm:w-1/3">
                          <div className="text-sm text-gray-500 mb-1">Chegada</div>
                          <div className="text-2xl font-bold text-voah-teal">{finalDestination}</div>
                          {finalArrivalDateTime && (
                            <div className="text-sm text-gray-700">
                              {formatDate(finalArrivalDateTime)}
                              <span className="mx-1">•</span>
                              {formatTime(finalArrivalDateTime)}
                            </div>
                          )}
                          {finalArrivalTerminal && (
                            <div className="text-xs text-gray-500 mt-1">Terminal {finalArrivalTerminal}</div>
                          )}
                        </div>
                      </div>
                      
                      {/* Connections section - only show if expanded */}
                      {hasConnections && isExpanded && (
                        <div className="mt-6 border-t pt-4">
                          <h4 className="text-lg font-medium text-gray-800 mb-4 text-center md:text-left">Detalhes do Itinerário</h4>
                          <div className="space-y-6">
                            {/* Main flight segment */}
                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                              <div className="flex items-center mb-3 justify-center md:justify-start">
                                <div className="bg-voah-teal text-white text-xs font-medium rounded-full w-6 h-6 flex items-center justify-center mr-2">
                                  1
                                </div>
                                <div className="text-sm font-medium text-gray-800">
                                  {flight.airline || 'Companhia não especificada'} • {flight.flightNumber || 'Voo não especificado'}
                                </div>
                              </div>
                              
                              <div className="flex flex-col items-center md:items-start md:flex-row justify-between">
                                {/* Main flight departure */}
                                <div className="flex-1 mb-3 md:mb-0 text-center md:text-left">
                                  <div className="text-xs text-gray-500 mb-1">Partida</div>
                                  <div className="text-base font-medium text-voah-teal">{flight.departureAirport}</div>
                                  {flight.departureDateTime && (
                                    <div className="text-xs text-gray-700">
                                      {formatDate(flight.departureDateTime)} • {formatTime(flight.departureDateTime)}
                                    </div>
                                  )}
                                  {flight.departureTerminal && (
                                    <div className="text-xs text-gray-500">Terminal {flight.departureTerminal}</div>
                                  )}
                                </div>
                                
                                {/* Main flight duration */}
                                <div className="flex-1 flex flex-col items-center justify-center py-1 mb-3 md:mb-0">
                                  <div className="relative w-full flex items-center justify-center">
                                    <div className="h-0.5 bg-gray-300 w-full absolute"></div>
                                    <ArrowRight className="text-voah-teal z-10 h-4 w-4 bg-white rounded-full p-0.5 shadow-sm" />
                                  </div>
                                  <div className="text-xs text-gray-700 mt-1">
                                    {flight.flightDuration || 'Duração não especificada'}
                                  </div>
                                </div>
                                
                                {/* Main flight arrival */}
                                <div className="flex-1 text-center md:text-right">
                                  <div className="text-xs text-gray-500 mb-1">Chegada</div>
                                  <div className="text-base font-medium text-voah-teal">{flight.arrivalAirport}</div>
                                  {flight.arrivalDateTime && (
                                    <div className="text-xs text-gray-700">
                                      {formatDate(flight.arrivalDateTime)} • {formatTime(flight.arrivalDateTime)}
                                    </div>
                                  )}
                                  {flight.arrivalTerminal && (
                                    <div className="text-xs text-gray-500">Terminal {flight.arrivalTerminal}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Connection segments */}
                            {flight.connections.map((connection, index) => (
                              <div key={connection.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                {/* Add waiting time display for connections */}
                                {index === 0 ? (
                                  <div className="flex items-center justify-center mb-3 bg-gray-100 rounded-md py-2">
                                    <Clock className="h-4 w-4 text-voah-teal mr-2" />
                                    <span className="text-sm text-gray-700">
                                      Tempo de espera: {
                                        connection.waitingTime || 
                                        (flight.arrivalDateTime && connection.departureDateTime ? 
                                          calculateWaitTime(flight as unknown as Connection, connection as Connection) : 
                                          'Não disponível')
                                      }
                                    </span>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center mb-3 bg-gray-100 rounded-md py-2">
                                    <Clock className="h-4 w-4 text-voah-teal mr-2" />
                                    <span className="text-sm text-gray-700">
                                      Tempo de espera: {
                                        connection.waitingTime || 
                                        (flight.connections[index - 1].arrivalDateTime && connection.departureDateTime ? 
                                          calculateWaitTime(flight.connections[index - 1] as Connection, connection as Connection) : 
                                          'Não disponível')
                                      }
                                    </span>
                                  </div>
                                )}
                                <div className="flex items-center mb-3 justify-center md:justify-start">
                                  <div className="bg-voah-teal text-white text-xs font-medium rounded-full w-6 h-6 flex items-center justify-center mr-2">
                                    {index + 2}
                                  </div>
                                  <div className="text-sm font-medium text-gray-800">
                                    {connection.airline || 'Companhia não especificada'} • {connection.flightNumber || 'Voo não especificado'}
                                  </div>
                                </div>
                                
                                <div className="flex flex-col items-center md:items-start md:flex-row justify-between">
                                  {/* Connection departure */}
                                  <div className="flex-1 mb-3 md:mb-0 text-center md:text-left">
                                    <div className="text-xs text-gray-500 mb-1">Partida</div>
                                    <div className="text-base font-medium text-voah-teal">{connection.departureAirport}</div>
                                    {connection.departureDateTime && (
                                      <div className="text-xs text-gray-700">
                                        {formatDate(connection.departureDateTime)} • {formatTime(connection.departureDateTime)}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Connection duration */}
                                  <div className="flex-1 flex flex-col items-center justify-center py-1 mb-3 md:mb-0">
                                    <div className="relative w-full flex items-center justify-center">
                                      <div className="h-0.5 bg-gray-300 w-full absolute"></div>
                                      <ArrowRight className="text-voah-teal z-10 h-4 w-4 bg-white rounded-full p-0.5 shadow-sm" />
                                    </div>
                                    <div className="text-xs text-gray-700 mt-1">
                                      {connection.flightDuration || 'Duração não especificada'}
                                    </div>
                                  </div>
                                  
                                  {/* Connection arrival */}
                                  <div className="flex-1 text-center md:text-right">
                                    <div className="text-xs text-gray-500 mb-1">Chegada</div>
                                    <div className="text-base font-medium text-voah-teal">{connection.arrivalAirport}</div>
                                    {connection.arrivalDateTime && (
                                      <div className="text-xs text-gray-700">
                                        {formatDate(connection.arrivalDateTime)} • {formatTime(connection.arrivalDateTime)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Display total journey time */}
                          <div className="mt-4 flex items-center justify-center md:justify-end">
                            <div className="flex items-center bg-voah-teal/10 px-3 py-1 rounded-full">
                              <Clock className="h-4 w-4 text-voah-teal mr-2" />
                              <span className="text-sm font-medium text-voah-teal">Tempo total: {calculateTotalFlightTime(flight)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Additional flight information */}
                      {flight.notes && (
                        <div className="mt-4 text-sm text-gray-700 border-t border-gray-200 pt-4">
                          <h4 className="font-medium mb-2">Observações</h4>
                          <p>{flight.notes}</p>
                        </div>
                      )}
                      
                      {/* Flight images carousel */}
                      {flight.images && flight.images.length > 0 && (
                        <div className="mt-6 border-t border-gray-200 pt-4">
                          <h4 className="font-medium mb-3">Imagens</h4>
                          <Carousel className="w-full">
                            <CarouselContent>
                        {flight.images.map((image, idx) => (
                                <CarouselItem key={idx} className="basis-full md:basis-1/2 lg:basis-1/3">
                                  <div className="relative h-48 rounded-lg overflow-hidden">
                            <img 
                              src={image} 
                              alt={`Imagem do voo ${idx + 1}`} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                                </CarouselItem>
                        ))}
                            </CarouselContent>
                            <CarouselPrevious className="left-2" />
                            <CarouselNext className="right-2" />
                          </Carousel>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper function to get unified items sorted by globalOrder
export function getUnifiedItems({
  blocks = [],
  tours = [],
  transportations = [],
  restaurants = [],
  accommodations = [],
  flights = [],
  flightEvents = [],
  accommodationEvents = [],
  showAccommodations = false
}: {
  blocks?: QuotationBlock[];
  tours?: TourInfo[];
  transportations?: TransportationInfo[];
  restaurants?: RestaurantInfo[];
  accommodations?: AccommodationInfo[];
  flights?: FlightInfo[];
  flightEvents?: FlightEvent[];
  accommodationEvents?: AccommodationEvent[];
  showAccommodations?: boolean;
}) {
  const items: any[] = [];
  
  // Add block items
  blocks.forEach(block => {
    items.push({
      ...block,
      type: 'block',
      globalOrder: block.globalOrder ?? 999999
    });
  });

  // Add tour items
  tours.forEach(tour => {
    items.push({
      ...tour,
      type: 'tour',
      globalOrder: tour.globalOrder ?? 999999
    });
  });
  
  // Add transportation items
  if (transportations && transportations.length > 0) {
    transportations.forEach(transportation => {
      items.push({
        ...transportation,
        type: 'transportation',
        globalOrder: transportation.globalOrder ?? 999999
      });
    });
  }
  
  // Add restaurant items
  if (restaurants && restaurants.length > 0) {
    restaurants.forEach(restaurant => {
      items.push({
        ...restaurant,
        type: 'restaurant',
        globalOrder: restaurant.globalOrder ?? 999999
      });
    });
  }
  
  // Add flight items with departure and arrival events
  const flightItems: any[] = [];
  
  if (flights && flights.length > 0) {
    // Add flights as separate departure and arrival events ONLY if showAsSeparateEvents is true
    flights.forEach((flight, idx) => {
      // Only process flights with showAsSeparateEvents enabled
      if (flight.showAsSeparateEvents) {
      // If flight has departureDateTime and departureCoordinates, add as departure event
      if (flight.departureDateTime && flight.departureCoordinates) {
        const validDepartureCoords = flight.departureCoordinates.lat !== undefined && 
                                     flight.departureCoordinates.lng !== undefined &&
                                     !isNaN(flight.departureCoordinates.lat) && 
                                     !isNaN(flight.departureCoordinates.lng);
        
        if (validDepartureCoords) {
          flightItems.push({
            ...flight,
            id: `${flight.id}_departure`,
            coordinates: flight.departureCoordinates,
            type: 'flight-departure',
            location: flight.departureAirport,
            globalOrder: flight.departureGlobalOrder ?? flight.globalOrder ?? 999999
          });
        }
      }
      
      // If flight has arrivalDateTime and arrivalCoordinates, add as arrival event
      if (flight.arrivalDateTime && flight.arrivalCoordinates) {
        const validArrivalCoords = flight.arrivalCoordinates.lat !== undefined && 
                                   flight.arrivalCoordinates.lng !== undefined &&
                                   !isNaN(flight.arrivalCoordinates.lat) && 
                                   !isNaN(flight.arrivalCoordinates.lng);
        
        if (validArrivalCoords) {
          flightItems.push({
            ...flight,
            id: `${flight.id}_arrival`,
            coordinates: flight.arrivalCoordinates,
            type: 'flight-arrival',
            location: flight.arrivalAirport,
            globalOrder: flight.arrivalGlobalOrder ?? flight.globalOrder ?? 999999
          });
          }
        }
      }
    });
  }
  
  // Add FlightEvents
  if (flightEvents && flightEvents.length > 0) {
    // Add each flight event, enriching with information from associated flight if available
    flightEvents.forEach(flightEvent => {
      // Find associated flight
      const flight = flights.find(f => f.id === flightEvent.flightId);
      
      if (flight) {
        // Get coordinates, dates, and location based on departure/arrival status
        const coordinates = flightEvent.isDeparture 
          ? flight.departureCoordinates 
          : flight.arrivalCoordinates;
          
        const location = flightEvent.isDeparture
          ? flight.departureAirport
          : flight.arrivalAirport;
          
        // Get date and time information
        const dateTime = flightEvent.isDeparture
          ? flight.departureDateTime
          : flight.arrivalDateTime;
          
        // For arrivals with connections, use info from the last connection
        if (!flightEvent.isDeparture && flight.connections && flight.connections.length > 0) {
          const lastConnection = flight.connections[flight.connections.length - 1];
          
          // Date/time for the connection
          const connectionDateTime = lastConnection.arrivalDateTime;
          
          flightItems.push({
            ...flightEvent,
            id: flightEvent.id,
            coordinates: coordinates || null,
            type: flightEvent.isDeparture ? 'flight-departure' : 'flight-arrival',
            location: lastConnection.arrivalAirport || location,
            flight: flight,
            flightId: flight.id,
            airline: lastConnection.airline || flight.airline,
            flightNumber: lastConnection.flightNumber || flight.flightNumber,
            departureAirport: flight.departureAirport,
            arrivalAirport: lastConnection.arrivalAirport || flight.arrivalAirport,
            departureDateTime: flight.departureDateTime,
            arrivalDateTime: connectionDateTime,
            globalOrder: flightEvent.globalOrder ?? 999999
          });
        } else {
          // For departures or arrivals without connections
          flightItems.push({
            ...flightEvent,
            id: flightEvent.id,
            coordinates: coordinates || null,
            type: flightEvent.isDeparture ? 'flight-departure' : 'flight-arrival',
            location: location,
            flight: flight,
            flightId: flight.id,
            airline: flight.airline,
            flightNumber: flight.flightNumber,
            departureAirport: flight.departureAirport,
            arrivalAirport: flight.arrivalAirport,
            departureDateTime: flight.departureDateTime,
            arrivalDateTime: flight.arrivalDateTime,
            globalOrder: flightEvent.globalOrder ?? 999999
          });
        }
      } else {
        // Still add the event even if associated flight isn't found
        flightItems.push({
          ...flightEvent,
          id: flightEvent.id,
          type: flightEvent.isDeparture ? 'flight-departure' : 'flight-arrival',
          globalOrder: flightEvent.globalOrder ?? 999999
        });
      }
    });
  }
  
  // Definir arrays para diferentes tipos de acomodações
  let finalAccommodationItems: any[] = [];
  const processedEventItems: any[] = [];
  
  // Processar eventos de acomodação independentemente de showAccommodations
  if (accommodationEvents && accommodationEvents.length > 0) {
    accommodationEvents.forEach(accEvent => {
      const accommodationParent = accommodations.find(acc => acc.id === accEvent.accommodationId);
      
      // Adicionar evento de hospedagem mesmo se o parent não for encontrado
      if (accommodationParent) {
        const isCheckIn = accEvent.isCheckIn;
        processedEventItems.push({
          id: accEvent.id,
          type: isCheckIn ? 'accommodation-checkin' : 'accommodation-checkout',
          globalOrder: accEvent.globalOrder ?? accommodationParent.globalOrder ?? 999999,
          name: accommodationParent.name,
          location: accommodationParent.location,
          images: accommodationParent.images,
          coordinates: accommodationParent.coordinates,
          date: isCheckIn ? accommodationParent.checkInDate : accommodationParent.checkOutDate,
          time: isCheckIn ? accommodationParent.checkInTime : accommodationParent.checkOutTime,
          notes: accEvent.notes || (isCheckIn ? accommodationParent.checkInNotes : accommodationParent.checkOutNotes),
          accommodation: { ...accommodationParent },
          accommodationId: accommodationParent.id,
          eventType: isCheckIn ? 'checkin' : 'checkout',
        });
      } else {
        // Adicionar evento mesmo se a hospedagem não for encontrada
        // Isso garante que o evento será visível mesmo sem hospedagem associada
        const isCheckIn = accEvent.isCheckIn;
        processedEventItems.push({
          id: accEvent.id,
          type: isCheckIn ? 'accommodation-checkin' : 'accommodation-checkout',
          globalOrder: accEvent.globalOrder ?? 999999,
          name: accEvent.title || (isCheckIn ? 'Check-in' : 'Check-out'),
          // Usar o título do evento como conteúdo principal
          location: 'Local não especificado',
          notes: accEvent.notes || '',
          accommodationId: accEvent.accommodationId, // Manter o ID mesmo que não tenha sido encontrado
          eventType: isCheckIn ? 'checkin' : 'checkout',
        });
        
        console.log('[QuotationView] Evento de hospedagem sem hospedagem associada:', {
          eventId: accEvent.id,
          accommodationId: accEvent.accommodationId,
          isCheckIn: accEvent.isCheckIn,
          title: accEvent.title
        });
      }
    });
  }
  
  // Adicionar eventos de acomodação processados sempre
  finalAccommodationItems = [...processedEventItems];
  
  // Adicionar acomodações regulares apenas se showAccommodations for true
  if (showAccommodations && accommodations && accommodations.length > 0) {
    // Se não houver eventos processados, adicionar as acomodações completas
    if (processedEventItems.length === 0) {
      accommodations.forEach(accommodation => {
        finalAccommodationItems.push({
          ...accommodation,
          id: accommodation.id,
          type: 'accommodation',
          globalOrder: accommodation.globalOrder ?? 999999,
          name: accommodation.name,
          location: accommodation.location,
        });
      });
    }
  }
  
  // Combine all items
  const allItems = [...items, ...flightItems, ...finalAccommodationItems];
  
  // Sort by globalOrder - ensures consistent ordering between desktop and mobile
  allItems.sort((a, b) => a.globalOrder - b.globalOrder);
  
  return allItems;
}

function PriceTab({ quotation }: { quotation: Quotation }) {
  if (!quotation.priceDetails) {
    return (
      <div className="p-3 sm:p-6 lg:p-8">
        <div className="bg-gray-100 p-4 rounded-md text-center text-gray-500">
          Sem informações de preço disponíveis.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8">
      <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none prose-headings:text-voah-teal prose-headings:font-medium prose-p:text-gray-700 prose-strong:text-gray-800 prose-strong:font-medium prose-a:text-voah-teal prose-a:no-underline hover:prose-a:underline prose-li:text-gray-700 prose-li:marker:text-voah-teal text-left">
        <div 
          dangerouslySetInnerHTML={{ 
            __html: quotation.priceDetails
              .replace(/style="[^"]*"/g, '')
              .replace(/style='[^']*'/g, '')
              .replace(/<p>/g, '<p class="mb-4 px-2 py-1">')
              .replace(/<p class="mb-4 px-2 py-1">(\s*)<\/p>/g, '')
          }} 
          className="text-[14px] sm:text-[16px] leading-loose text-left" 
        />
      </div>
    </div>
  );
}

export default function QuotationView() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [linkedTrip, setLinkedTrip] = useState<{ start_date?: string | null; end_date?: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("boas-vindas");
  const [activePageTitle, setActivePageTitle] = useState<string>("");
  const [showTabArrows, setShowTabArrows] = useState(false);
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [showMobileMap, setShowMobileMap] = useState(false);
  // Estado para controlar qual item está expandido (apenas um por vez)
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  // Estado para controlar o colapso da descrição da página
  const [collapsedDescriptions, setCollapsedDescriptions] = useState<Record<string, boolean>>({});
  // Estado para controlar a visibilidade do modal de configurações
  const [showConfigModal, setShowConfigModal] = useState(false);
  // Estado para controlar grupos de recomendações colapsados
  const [collapsedRecommendationGroups, setCollapsedRecommendationGroups] = useState<Record<string, boolean>>(() => {
    // Criar um objeto com true (colapsado) para todos os grupos de recomendações
    const initialState: Record<string, boolean> = {};
    if (quotation?.recommendationGroups) {
      quotation.recommendationGroups.forEach(group => {
        initialState[group.id] = true;
      });
    }
    return initialState;
  });
  // Estado temporário para armazenar configurações modificadas no modal
  const [tempConfig, setTempConfig] = useState<Partial<Quotation> | null>(null);
  // Estado para armazenar o status de autenticação do usuário
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  const coverDateRange = useMemo(() => {
    if (!quotation) return '';
    const { startDate, endDate } = resolveCoverDates(quotation, linkedTrip);
    return formatCoverDateRange(startDate, endDate);
  }, [quotation, linkedTrip]);
  
  // Verificar se o usuário está autenticado (é admin)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setIsAdmin(!!user);
      } catch (error) {
        console.error('Erro ao verificar autenticação:', error);
        setIsAdmin(false);
      }
    };
    
    // Verificar autenticação na montagem do componente
    checkAuth();
    
    // Configurar listener para mudanças na autenticação
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setIsAdmin(!!session?.user);
      }
    );
    
    // Cleanup listener
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);
  
  // Reset do estado temporário quando o modal é aberto
  useEffect(() => {
    if (showConfigModal && quotation) {
      // Inicializar com os valores atuais da cotação
      setTempConfig({
        useDayMode: quotation.useDayMode,
        usePageDropdown: quotation.usePageDropdown,
        showMapLines: quotation.showMapLines,
        showAccommodationsInItinerary: quotation.showAccommodationsInItinerary,
        showPriceTab: quotation.showPriceTab,
        showRecommendationsTab: quotation.showRecommendationsTab,
        showAccommodationsTab: quotation.showAccommodationsTab,
        showFlightsTab: quotation.showFlightsTab,
        accommodationsTabName: quotation.accommodationsTabName,
        recommendationsTabName: quotation.recommendationsTabName,
        priceTabName: quotation.priceTabName,
        flightsTabName: quotation.flightsTabName,
        startDate: quotation.startDate,
      });
    } else {
      // Limpar quando o modal é fechado
      setTempConfig(null);
    }
  }, [showConfigModal, quotation]);
  
  // Handler function to navigate to the Accommodations tab and scroll to the accommodation
  const handleViewAccommodationDetails = useCallback((accommodationId: string) => {
    setActiveTab('hospedagens');
    
    // Use setTimeout to ensure the tab content is rendered before scrolling
    setTimeout(() => {
      const accommodationElement = document.getElementById(`accommodation-${accommodationId}`);
      if (accommodationElement) {
        accommodationElement.scrollIntoView({ behavior: 'smooth' });
        
        // Optional: highlight the accommodation card
        accommodationElement.classList.add('ring-2', 'ring-voah-teal', 'ring-offset-2');
        setTimeout(() => {
          accommodationElement.classList.remove('ring-2', 'ring-voah-teal', 'ring-offset-2');
        }, 2000);
      }
    }, 100);
  }, []);
  
  // Adicionar handler para visualizar detalhes do voo
  const handleViewFlightDetails = useCallback((flightId: string) => {
    // Verificar se quotation existe antes de acessar suas propriedades
    if (!quotation) return;
    
    // Encontrar o voo em todas as páginas
    for (const page of quotation.pages) {
      const flight = page.flights?.find(f => f.id === flightId);
      if (flight) {
        // Navegar para a aba de voos
        setActiveTab('voos');
        
        // Use setTimeout para garantir que a aba de voos seja renderizada antes de tentar scroll
        setTimeout(() => {
          const flightElement = document.getElementById(`flight-${flightId}`);
          if (flightElement) {
            flightElement.scrollIntoView({ behavior: 'smooth' });
            
            // Opcional: destacar o card do voo
            flightElement.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
            setTimeout(() => {
              flightElement.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
            }, 2000);
          }
        }, 100);
        
        return;
      }
    }
  }, [quotation]);
  
  // Função para expandir/colapsar um item
  const toggleItem = (itemId: string) => {
    setExpandedItemId(expandedItemId === itemId ? null : itemId);
  };
  
  // Função para expandir/colapsar a descrição de uma página
  const toggleDescription = (pageId: string) => {
    setCollapsedDescriptions(prev => ({
      ...prev,
      [pageId]: !prev[pageId]
    }));
  };
  
  // Função para alternar o colapso de um grupo de recomendações
  const toggleRecommendationGroup = (groupId: string) => {
    setCollapsedRecommendationGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };
  
  // Função para atualizar configurações temporárias no modal
  const updateTempConfig = (field: keyof Quotation, value: any) => {
    if (!tempConfig) return;
    
    setTempConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Função para aplicar as configurações temporárias à cotação
  const applyConfigChanges = () => {
    if (!quotation || !tempConfig) return;
    
    // Criar uma cópia da cotação com os campos atualizados
    const updatedQuotation = {
      ...quotation,
      ...tempConfig,
      updatedAt: new Date()
    };
    
    // Caso especial para useDayMode - atualizar títulos de página se necessário
    if (tempConfig.useDayMode !== undefined && tempConfig.useDayMode !== quotation.useDayMode) {
      if (tempConfig.useDayMode === true) {
        // Alterando para modo dias - atualizar títulos das páginas
        updatedQuotation.pages = updatedQuotation.pages.map((page, index) => {
          let dayTitle = `Dia ${index + 1}`;
          
          // Se temos uma data de início definida, calcular datas sequenciais
          if (updatedQuotation.startDate && index === 0) {
            // Primeiro dia recebe a data exata de início
            dayTitle = `Dia ${index + 1} (${formatSafeDate(updatedQuotation.startDate, 'dd/MM')})`;
          } else if (updatedQuotation.startDate && index > 0) {
            // Calcular data sequencial para os dias subsequentes
            const date = new Date(updatedQuotation.startDate);
            date.setDate(date.getDate() + index);
            dayTitle = `Dia ${index + 1} (${formatSafeDate(date, 'dd/MM')})`;
          }
          
          return {
            ...page,
            // Armazenar o título atual como pageTitle se ainda não estiver definido
            pageTitle: page.pageTitle || page.title,
            // Definir dayTitle e atualizar o título visível para o formato de dia
            dayTitle: dayTitle,
            title: dayTitle // Para compatibilidade com versões anteriores
          };
        });
      } else {
        // Voltando para o modo página - restaurar títulos originais das páginas
        updatedQuotation.pages = updatedQuotation.pages.map(page => {
          return {
            ...page,
            // Restaurar o título da página a partir do pageTitle armazenado ou usar um padrão
            title: page.pageTitle || page.title,
            // Manter o dayTitle atual para quando voltar ao modo dia
            dayTitle: page.dayTitle
          };
        });
      }
    }
    
    // Caso especial para alteração do activeTab quando mudamos para modo de dropdown
    if (tempConfig.usePageDropdown !== undefined && 
        tempConfig.usePageDropdown !== quotation.usePageDropdown && 
        activeTab.startsWith('page-')) {
      const pageId = activeTab.replace('page-', '');
      const page = updatedQuotation.pages.find(p => p.id === pageId);
      if (page) {
        setActivePageTitle(page.title || `Página ${updatedQuotation.pages.indexOf(page) + 1}`);
      }
    }
    
    // Caso especial para alteração do activeTab quando desabilitamos a aba de preços
    if (tempConfig.showPriceTab !== undefined && 
        tempConfig.showPriceTab === false && 
        quotation.showPriceTab === true && 
        activeTab === 'precos') {
      // Voltar para a aba de boas-vindas se a aba de preços for desabilitada
      setActiveTab('boas-vindas');
    }
    
    // Caso especial para alteração do activeTab quando desabilitamos a aba de recomendações
    if (tempConfig.showRecommendationsTab !== undefined && 
        tempConfig.showRecommendationsTab === false && 
        quotation.showRecommendationsTab === true && 
        activeTab === 'recomendacoes') {
      // Voltar para a aba de boas-vindas se a aba de recomendações for desabilitada
      setActiveTab('boas-vindas');
    }
    
    // Caso especial para alteração do activeTab quando desabilitamos a aba de voos
    if (tempConfig.showFlightsTab !== undefined && 
        tempConfig.showFlightsTab === false && 
        quotation.showFlightsTab !== false && 
        activeTab === 'voos') {
      // Voltar para a aba de boas-vindas se a aba de voos for desabilitada
      setActiveTab('boas-vindas');
    }
    
    // Atualizar o estado com a cotação modificada
    setQuotation(updatedQuotation);
    
    // Salvar no localStorage
    localStorage.setItem('voah_quotation_draft', JSON.stringify(updatedQuotation));
    
    // Fechar o modal após aplicar as alterações
    setShowConfigModal(false);
    toast({
      title: "Configurações salvas",
      description: "As configurações foram salvas com sucesso!",
    });
  };
  
  // Função para extrair blocos e tours de uma página específica
  const extractPageData = (page: QuotationPage): { 
    blocks: QuotationBlock[], 
    tours: TourInfo[], 
    transportations: TransportationInfo[], 
    restaurants: RestaurantInfo[],
    accommodations: AccommodationInfo[],
    flights: FlightInfo[],
    flightEvents: FlightEvent[],
    accommodationEvents: AccommodationEvent[]
  } => {
    return {
      blocks: page.blocks || [],
      tours: page.tours || [],
      transportations: page.transportations || [],
      restaurants: page.restaurants || [],
      accommodations: page.accommodations || [],
      flights: page.flights || [],
      flightEvents: page.flightEvents || [],
      accommodationEvents: page.accommodationEvents || []
    };
  };
  
  // Extrair todos os voos de todas as páginas
  const getAllFlights = useMemo(() => {
    if (!quotation || !quotation.pages) return [];
    
    return quotation.pages.reduce((allFlights, page) => {
      // Adicionar a origem da página para identificação
      const pageFlights = (page.flights || []).map(flight => ({
        ...flight,
        _page: page.title || `Página ${page.id.substring(0, 8)}`
      }));
      return [...allFlights, ...pageFlights];
    }, [] as (FlightInfo & { _page?: string })[]);
  }, [quotation]);

  console.log('[QuotationView] Total de voos em toda a cotação:', getAllFlights.length);
  
  // Extrair todas as acomodações de todas as páginas
  const getAllAccommodations = useMemo(() => {
    if (!quotation || !quotation.pages) return [];
    
    return quotation.pages.reduce((allAccommodations, page) => {
      // Adicionar a origem da página para identificação
      const pageAccommodations = (page.accommodations || []).map(accommodation => ({
        ...accommodation,
        _page: page.title || `Página ${page.id.substring(0, 8)}`
      }));
      return [...allAccommodations, ...pageAccommodations];
    }, [] as (AccommodationInfo & { _page?: string })[]);
  }, [quotation]);

  console.log('[QuotationView] Total de acomodações em toda a cotação:', getAllAccommodations.length);
  
  // Função para salvar as recomendações no localStorage
  const saveRecommendationsToCache = useCallback((quotationId: string, recommendations: UnifiedItem[]) => {
    try {
      if (!quotationId || !recommendations || recommendations.length === 0) return;
      
      // Obter o cache atual ou inicializar
      const cacheName = `voah_recommendations_cache`;
      const currentCache = localStorage.getItem(cacheName);
      const recommendationsCache = currentCache ? JSON.parse(currentCache) : {};
      
      // Adicionar ao cache com timestamp
      recommendationsCache[quotationId] = {
        items: recommendations,
        timestamp: new Date().getTime(),
      };
      
      // Salvar o cache atualizado
      localStorage.setItem(cacheName, JSON.stringify(recommendationsCache));
      console.log(`Cached ${recommendations.length} recommendations for quotation ${quotationId}`);
    } catch (error) {
      console.error('Erro ao salvar recomendações no cache:', error);
    }
  }, []);

  // Função para carregar recomendações do localStorage
  const loadRecommendationsFromCache = useCallback((quotationId: string): UnifiedItem[] | null => {
    try {
      if (!quotationId) return null;
      
      const cacheName = `voah_recommendations_cache`;
      const currentCache = localStorage.getItem(cacheName);
      if (!currentCache) return null;
      
      const recommendationsCache = JSON.parse(currentCache);
      const cachedData = recommendationsCache[quotationId];
      
      if (!cachedData || !cachedData.items) return null;
      
      // Verificar se o cache não está muito antigo (24 horas)
      const now = new Date().getTime();
      const maxAge = 24 * 60 * 60 * 1000; // 24 horas em milissegundos
      
      if (now - cachedData.timestamp > maxAge) {
        console.log('Cache de recomendações expirado, será recarregado');
        return null;
      }
      
      console.log(`Loaded ${cachedData.items.length} recommendations from cache for quotation ${quotationId}`);
      return cachedData.items;
    } catch (error) {
      console.error('Erro ao carregar recomendações do cache:', error);
      return null;
    }
  }, []);
  
  // Efeito para carregar recomendações faltantes quando necessário
  useEffect(() => {
    // Skip if already loading or no quotation
    if (loading || !quotation || !quotation.recommendationGroups) return;
    
    // Coletar todos os IDs de itens dos grupos de recomendação
    const itemIds = quotation.recommendationGroups
      .flatMap(group => group.itemIds || [])
      .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates
    
    // Verificar quais IDs já temos e quais precisamos buscar
    const existingIds = (quotation.recommendations || []).map(item => item.id);
    const missingIds = itemIds.filter(id => !existingIds.includes(id));
    
    // Se não temos IDs faltantes, não precisamos buscar nada
    if (missingIds.length === 0) {
      console.log('Todos os itens de recomendação já estão carregados:', existingIds.length);
      return;
    }
    
    console.log(`Buscando ${missingIds.length} itens de recomendação faltantes:`, missingIds);
    
    const fetchMissingRecommendations = async () => {
      try {
        // CORREÇÃO: Tentar buscar itens individualmente com sintaxe correta
        let foundItems = [];
        
        for (const itemId of missingIds) {
          try {
            // Usar sintaxe correta para o Supabase
            const { data, error } = await supabase
              .from('library_items')
              .select('*')
              .eq('id', itemId);  // Sintaxe correta: .eq não id=eq
            
            if (error) {
              console.error(`Erro Supabase ao buscar item ${itemId}:`, error);
              continue;
            }
            
            if (!data || data.length === 0) {
              console.warn(`Item não encontrado: ${itemId}`);
              continue;
            }
            
            foundItems.push(data[0]);
          } catch (e) {
            console.error(`Erro ao buscar item ${itemId}:`, e);
          }
        }
        
        console.log(`Encontrados ${foundItems.length} de ${missingIds.length} itens de recomendação`);
        
        if (foundItems.length > 0) {
          // Converter os itens do Supabase para o formato esperado pela aplicação
          const recommendationItems = foundItems.map((item: any) => {
            // Verificar tipo de item
            if (!item.type) {
              console.error(`Item sem tipo definido:`, item);
              return null;
            }
            
            // Garantir que o item.data existe
            const itemData = item.data || {};
            
            // Criar o objeto com a estrutura correta
            return {
              id: item.id,
              type: item.type,
              globalOrder: 0,
              // Construir explicitamente o objeto do tipo correto
              [item.type]: {
                ...itemData,
                id: item.id,
                name: item.name || itemData.name || `${item.type} sem nome`,
                description: itemData.description || '',
                location: itemData.location || '',
                // Garantir que images é sempre um array
                images: Array.isArray(itemData.images) ? itemData.images : []
              }
            };
          }).filter(Boolean); // Remover itens nulos
          
          console.log(`Processados ${recommendationItems.length} itens de recomendação`);
          
          // Atualizar a cotação com as recomendações
          setQuotation(prev => {
            if (!prev) return null;
            
            // Mesclar com recomendações existentes, evitando duplicatas
            const existingItems = prev.recommendations || [];
            const mergedItems = [...existingItems];
            
            // Adicionar apenas os novos itens
            recommendationItems.forEach(item => {
              if (!mergedItems.some(existing => existing.id === item.id)) {
                mergedItems.push(item);
              }
            });
            
            console.log(`Total de itens após mesclagem: ${mergedItems.length}`);
            
            // Salvar no cache após processamento
            saveRecommendationsToCache(prev.id, mergedItems);
            
            return {
              ...prev,
              recommendations: mergedItems
            };
          });
        }
      } catch (err) {
        console.error('Erro ao processar itens de recomendação:', err);
      }
    };
    
    fetchMissingRecommendations();
  // ↓↓ IMPORTANTE: Adicionar 'missingIds' na lista de dependências para evitar loops ↓↓
  }, [quotation?.id, quotation?.recommendationGroups, loading, supabase, saveRecommendationsToCache]);

  // Efeito para salvar recomendações no cache quando há mudanças
  useEffect(() => {
    // Só salvar no cache se temos cotação e recomendações válidas
    if (quotation?.id && quotation?.recommendations?.length > 0) {
      // Usar um ID de sessão para evitar salvar repetidamente os mesmos dados
      const cacheKey = `saved_recommendations_${quotation.id}`;
      const lastSavedHash = sessionStorage.getItem(cacheKey);
      const currentHash = JSON.stringify(quotation.recommendations.map(rec => rec.id).sort());
      
      // Só salvar no cache se os dados mudaram
      if (lastSavedHash !== currentHash) {
        console.log(`Salvando ${quotation.recommendations.length} recomendações no cache`);
        saveRecommendationsToCache(quotation.id, quotation.recommendations);
        sessionStorage.setItem(cacheKey, currentHash);
      }
    }
  }, [quotation?.id, quotation?.recommendations]);

  useEffect(() => {
    const loadQuotation = async () => {
      try {
        // Se estamos buscando uma cotação específica pelo ID (não uma visualização de preview)
        if (id && id !== 'preview') {
          try {
            // Verificar se existe um rascunho no localStorage
            const localDraft = localStorage.getItem('voah_quotation_draft');
            let localQuotation;
            
            if (localDraft) {
              try {
                localQuotation = JSON.parse(localDraft);
                
                // Se o rascunho local for para a mesma cotação que estamos vendo
                if (localQuotation && localQuotation.id === id) {
                  // Usar o rascunho local diretamente, já que contém as alterações mais recentes
                  console.log('Usando rascunho local', localQuotation);
                  setQuotation(localQuotation);

                  if (localQuotation.tripId) {
                    const tripData = await tripService.getTripById(localQuotation.tripId);
                    setLinkedTrip(tripData);
                  } else {
                    setLinkedTrip(null);
                  }
                  
                  if (localQuotation.pages && localQuotation.pages.length > 0) {
                    const firstPage = localQuotation.pages[0];
                    setActivePageTitle(firstPage.title || `Página 1`);
                  }
                
                  setLoading(false);
                  return;
                }
              } catch (e) {
                console.error('Erro ao analisar rascunho local:', e);
              }
            }
            
            // Continuar com o carregamento normal do Supabase se não houver rascunho local
            const quotationData = await quotationService.getQuotationById(id);
            
            if (quotationData) {
              console.log('Carregando cotação do Supabase:', quotationData);
              
              // Mapear para o formato da aplicação
              const processedQuotation: Quotation = {
                id: quotationData.id,
                title: quotationData.title || '',
                client: quotationData.client || '',
                destination: quotationData.destination || [],
                ...mapQuotationDatesFromDb(quotationData.start_date, quotationData.end_date),
                status: 'draft',
                notes: quotationData.notes || '',
                pages: quotationData.pages || [],
                coverImages: quotationData.cover_images || [],
                createdAt: quotationData.created_at ? new Date(quotationData.created_at) : new Date(),
                updatedAt: quotationData.updated_at ? new Date(quotationData.updated_at) : new Date(),
                tripId: quotationData.trip_id,
                showRecommendationsTab: quotationData.show_recommendations_tab !== undefined 
                  ? quotationData.show_recommendations_tab 
                  : true,
                showPriceTab: quotationData.show_price_tab !== undefined 
                  ? quotationData.show_price_tab 
                  : true,
                recommendationsTabName: quotationData.recommendations_tab_name,
                recommendationGroups: (quotationData.recommendation_groups || []).map(group => {
                  // Log detalhado para cada grupo de recomendação sendo mapeado
                  console.log('DEBUG - Mapeando grupo de recomendação do banco:', {
                    id: group.id, 
                    title: group.title,
                    descriptionOriginal: group.description,
                    hasDescription: !!group.description,
                    descriptionLength: group.description?.length || 0
                  });
                  
                  // Preservar explicitamente todas as propriedades, incluindo a descrição
                  return {
                    id: group.id,
                    title: group.title,
                    description: group.description || '', // Garantir que a descrição seja preservada
                    order: group.order || 0,
                    itemIds: group.itemIds || []
                  };
                }),
                recommendations: [],  // Initialize with empty array, will populate below
                useDayMode: quotationData.use_day_mode,
                usePageDropdown: quotationData.use_page_dropdown,
                showMapLines: quotationData.show_map_lines,
                showAccommodationsInItinerary: quotationData.show_accommodations_in_itinerary,
                // Adicionar campos faltantes para os grupos de acomodação
                accommodationGroups: quotationData.accommodation_groups || [],
                useCustomAccommodationGroups: quotationData.use_custom_accommodation_groups || false,
                accommodationsTabName: quotationData.accommodations_tab_name,
                showAccommodationsTab: quotationData.show_accommodations_tab !== false,
                accommodationsDescription: quotationData.accommodations_description || '',
                // Adicionar campos para a aba de Voos
                showFlightsTab: quotationData.show_flights_tab !== false,
                flightsTabName: quotationData.flights_tab_name,
                showFlightsInItinerary: quotationData.show_flights_in_itinerary !== false,
                flightGroups: quotationData.flight_groups || [],
                useCustomFlightGroups: quotationData.use_custom_flight_groups || false,
                flightsDescription: quotationData.flights_description || '',
                // Adicionar campo para preços
                priceTabName: quotationData.price_tab_name,
                // Adicionar o campo priceDetails que estava faltando
                priceDetails: quotationData.price_details || '',
                // Adicionar campos para a aba customizada
                showCustomTab: quotationData.show_custom_tab,
                customTabName: quotationData.custom_tab_name,
                customTabGroups: quotationData.custom_tab_groups || [],
                // === CORREÇÃO: garantir que page_groups seja mapeado ===
                page_groups: quotationData.page_groups
                  ? {
                      groups: Array.isArray(quotationData.page_groups.groups) ? quotationData.page_groups.groups : [],
                      ungrouped: Array.isArray(quotationData.page_groups.ungrouped) ? quotationData.page_groups.ungrouped : [],
                      linearList: Array.isArray(quotationData.page_groups.linearList) ? quotationData.page_groups.linearList : undefined,
                    }
                  : { groups: [], ungrouped: [] },
              };
              
              // Tentar carregar recomendações do cache local primeiro
              let cachedRecommendations = null;
              if (Array.isArray(processedQuotation.recommendationGroups) && processedQuotation.recommendationGroups.length > 0) {
                // Log para diagnóstico
                const totalGroupItems = processedQuotation.recommendationGroups.reduce((total, group) => 
                  total + (Array.isArray(group.itemIds) ? group.itemIds.length : 0), 0);
                console.log(`Cotação tem ${Array.isArray(processedQuotation.recommendationGroups) ? processedQuotation.recommendationGroups.length : 0} grupos com total de ${totalGroupItems} itens`);
                
                // MODIFICADO: Ignorando cache e forçando busca direta do Supabase
                // Fetch library items for recommendation groups
                try {
                  // Get all unique item IDs from all groups
                  const itemIds = processedQuotation.recommendationGroups
                    .flatMap(group => Array.isArray(group.itemIds) ? group.itemIds : [])
                    .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates
                  
                  if (itemIds.length > 0) {
                    console.log('Fetching library items for recommendations:', itemIds);
                    console.log('MODIFICADO: Forçando busca direta do Supabase ignorando sistema de cache');
                    
                    // Fetch items from Supabase
                    const { data, error } = await supabase
                      .from('library_items')
                      .select('*')
                      .in('id', itemIds);
                    
                    if (!error && data && data.length > 0) {
                      console.log(`Supabase retornou ${data.length} itens para recomendações`, data);
                      
                      // Convert Supabase items to the format expected by the application
                      const recommendationItems = data.map((item: any) => {
                        // Create UnifiedItem format
                        return {
                          id: item.id,
                          type: item.type,
                          globalOrder: 0,
                          // Add appropriate property based on type
                          [item.type]: {
                            ...item.data,
                            id: item.id,
                            name: item.name || item.data?.name || '',
                            description: item.data?.description || '',
                            location: item.data?.location || '',
                            images: item.data?.images || []
                          }
                        };
                      });
                      
                      console.log('Loaded recommendation items:', recommendationItems);
                      processedQuotation.recommendations = recommendationItems;
                      
                      // Salvar as recomendações no cache
                      saveRecommendationsToCache(id, recommendationItems);
                    } else {
                      console.warn('Erro ao buscar itens de recomendação:', error);
                      console.warn('IDs de itens solicitados:', itemIds);
                      toast({
                        title: "Aviso",
                        description: "Algumas recomendações podem não estar disponíveis. Tente recarregar a página se necessário.",
                        variant: "destructive"
                      });
                    }
                  }
                } catch (err) {
                  console.error('Error processing recommendation items:', err);
                }
              }
              
              setQuotation(processedQuotation);

              if (processedQuotation.tripId) {
                const tripData = await tripService.getTripById(processedQuotation.tripId);
                setLinkedTrip(tripData);
              } else {
                setLinkedTrip(null);
              }
              
              // Always keep "boas-vindas" as the default tab
              if (processedQuotation.pages && processedQuotation.pages.length > 0) {
                const firstPage = processedQuotation.pages[0];
                setActivePageTitle(firstPage.title || `Página 1`);
              }
                
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error('Erro ao carregar cotação do Supabase:', error);
          }
        }
        
        // Se estivermos no modo preview ou com o ID 'preview', criar uma cotação de exemplo
        if (id === 'preview') {
        setTimeout(() => {
          // Create example quotation for demonstration
          const exampleQuotation: Quotation = {
            id: id || 'preview',
            title: 'Cotação Portugal e Espanha',
            client: 'Cliente Exemplo',
            destination: ['Portugal', 'Espanha'],
            startDate: '2023-11-15',
            endDate: '2023-11-25',
            status: 'draft',
            notes: 'Exemplo de cotação para demonstração.',
            coverImages: [
              'https://images.unsplash.com/photo-1580323956606-5e6943edafb5?q=80&w=2874&auto=format&fit=crop'
            ],
            useDayMode: false,
            usePageDropdown: false,
            showRecommendationsTab: true,
            recommendationGroups: [
              {
                id: 'group-1',
                title: 'Restaurantes em Lisboa',
                order: 0,
                itemIds: ['rec-1', 'rec-2']
              },
              {
                id: 'group-2',
                title: 'Passeios em Madrid',
                order: 1,
                itemIds: ['rec-3']
              }
            ],
            recommendations: [
              {
                id: 'rec-1',
                type: 'restaurant',
                globalOrder: 0,
                restaurant: {
                  id: 'rec-1',
                  name: 'Restaurante Belcanto',
                  location: 'Lisboa, Portugal',
                  description: 'Restaurante com duas estrelas Michelin do renomado chef José Avillez. Culinária portuguesa contemporânea em um ambiente elegante.',
                  images: ['https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&auto=format&fit=crop']
                }
              },
              {
                id: 'rec-2',
                type: 'restaurant',
                globalOrder: 1,
                restaurant: {
                  id: 'rec-2',
                  name: 'Time Out Market',
                  location: 'Lisboa, Portugal',
                  description: 'Mercado gastronômico com diversos restaurantes, onde você pode provar diferentes especialidades portuguesas.',
                  images: ['https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?w=800&auto=format&fit=crop']
                }
              },
              {
                id: 'rec-3',
                type: 'tour',
                globalOrder: 2,
                tour: {
                  id: 'rec-3',
                  name: 'Visita ao Museu do Prado',
                  location: 'Madrid, Espanha',
                  description: 'Um dos museus de arte mais importantes do mundo, com uma coleção impressionante de obras europeias do século XII ao XIX.',
                  images: ['https://images.unsplash.com/photo-1572953109213-3be62398ebaf?w=800&auto=format&fit=crop']
                }
              }
            ],
            pages: [
              {
                id: '1',
                title: 'Portugal',
                description: 'Portugal é um país com uma rica história, belas praias e ótima gastronomia.',
                blocks: [],
                images: ['https://images.unsplash.com/photo-1580323956606-5e6943edafb5?q=80&w=2874&auto=format&fit=crop'],
                showFlights: true,
                showAccommodations: true,
                showRestaurants: true,
                showTransportations: true,
                  showTours: true,
                  flights: [],
                accommodations: [],
                tours: [],
                restaurants: [],
                  transportations: [],
                  globalOrder: 0,
                  startDate: '2023-11-15',
                  endDate: '2023-11-18',
                  flightEvents: [],
                  accommodationEvents: [],
                  useCustomAccommodationGroups: false,
                  accommodationGroups: [],
                  showAccommodationsTab: true,
                  accommodationsTabName: "Hospedagens",
                  accommodationsDescription: ""
              }
            ],
            createdAt: new Date(),
            updatedAt: new Date(),
            // Adicionar campos para os grupos de acomodação no exemplo
            useCustomAccommodationGroups: false,
            accommodationGroups: [],
            showAccommodationsTab: true,
            accommodationsTabName: "Hospedagens",
            accommodationsDescription: "",
            // Adicionar campos para a aba de Voos
            showFlightsTab: true,
            flightsTabName: "Voos",
            showFlightsInItinerary: true,
            flightGroups: [],
            useCustomFlightGroups: false,
            flightsDescription: "",
            // Adicionar campo para preços
            priceTabName: "Preços"
          };
          
          setQuotation(exampleQuotation);
          setActivePageTitle('Portugal');
          setLoading(false);
          }, 500);
        } else {
          // Se não é preview e não encontrou uma cotação, mostrar erro
          toast({
            title: "Cotação não encontrada",
            description: "Não foi possível encontrar a cotação solicitada.",
            variant: "destructive"
          });
          setLoading(false);
          navigate('/admin/quotations');
        }
      } catch (error) {
        console.error('Error in loadQuotation:', error);
        setLoading(false);
        toast({
          title: "Erro ao carregar cotação",
          description: "Ocorreu um erro ao carregar a cotação.",
          variant: "destructive"
        });
      }
    };

    loadQuotation();
  }, [id, navigate, toast]);

  useEffect(() => {
    const checkTabsOverflow = () => {
      if (tabsListRef.current) {
        const { scrollWidth, clientWidth } = tabsListRef.current;
        const hasOverflow = scrollWidth > clientWidth;
        setShowTabArrows(hasOverflow);
        
        if (hasOverflow) {
          const isAtStart = tabsListRef.current.scrollLeft <= 10;
          const isAtEnd = tabsListRef.current.scrollLeft + clientWidth >= scrollWidth - 10;
          
          const container = tabsListRef.current.closest('.tabs-container');
          if (container) {
            container.classList.toggle('at-start', isAtStart);
            container.classList.toggle('at-end', isAtEnd);
          }
        }
      }
    };
    
    checkTabsOverflow();
    
    window.addEventListener('resize', checkTabsOverflow);
    
    return () => {
      window.removeEventListener('resize', checkTabsOverflow);
    };
  }, [quotation]);

  // Scroll to the active tab when it changes
  useEffect(() => {
    if (tabsListRef.current && activeTab) {
      // Skip scrolling to page tabs when using dropdown navigation
      if (!!quotation?.usePageDropdown && activeTab.startsWith('page-')) {
        return;
      }
      
      const activeTabElement = tabsListRef.current.querySelector(`[data-value="${activeTab}"]`);
      if (activeTabElement) {
        // Calculate the position to scroll to center the active tab
        const tabsContainer = tabsListRef.current;
        const tabElement = activeTabElement as HTMLElement;
        const containerWidth = tabsContainer.clientWidth;
        const tabOffsetLeft = tabElement.offsetLeft;
        const tabWidth = tabElement.offsetWidth;
        
        // Center the tab in the container
        const scrollPosition = tabOffsetLeft - (containerWidth / 2) + (tabWidth / 2);
        
        tabsContainer.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      }
    }
  }, [activeTab, quotation?.usePageDropdown]);

  const handleBackNavigation = () => {
    if (location.search.includes('preview=true') || id === 'preview') {
      if (quotation) {
        localStorage.setItem('voah_quotation_draft', JSON.stringify(quotation));
      }
      
      const editPath = quotation?.id && quotation.id !== 'preview' 
        ? `/admin/quotations/${quotation.id}`
        : '/admin/quotations/new';
      navigate(editPath);
    } else {
      navigate(`/admin/quotations/${id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-voah-cream">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-voah-teal mx-auto"></div>
          <p className="mt-4 text-voah-teal">Carregando sua Viagem...</p>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-voah-cream">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Cotação não encontrada</h2>
          <p className="mb-6 text-gray-600">A cotação que você está procurando não está disponível.</p>
          <Button 
            onClick={() => navigate(-1)}
            className="bg-voah-teal hover:bg-voah-teal-dark"
          >
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  // Format destination for display
  const destinationDisplay = typeof quotation.destination === 'string' 
    ? quotation.destination 
    : quotation.destination.join(', ');
    
  // Function to get client names either from the string or from the linked trip
  const formatClientNames = (clientsString: string | undefined) => {
    // If we have a linked trip, get client names from there
    if (quotation.tripId) {
      try {
        // Load trips from localStorage
        const savedTrips = localStorage.getItem('voah_trips');
        if (savedTrips) {
          const trips = JSON.parse(savedTrips);
          const linkedTrip = trips.find((trip: any) => trip.id === quotation.tripId);
          
          if (linkedTrip && linkedTrip.clientIds && linkedTrip.clientIds.length > 0) {
            // Load clients from localStorage
            const savedClients = localStorage.getItem('voah_clients');
            if (savedClients) {
              const clients = JSON.parse(savedClients);
              const tripClients = clients.filter((client: any) => 
                linkedTrip.clientIds.includes(client.id)
              );
              
              // Return full client names
              return tripClients
                .map((client: any) => client.name) // Use full name
                .join(', ');
            }
          }
        }
      } catch (error) {
        // Error silenciado
      }
    }
    
    // Fallback to the client string if no trip clients were found
    // Return exactly as stored, without any manipulation
    return clientsString || '';
  };
  
  // Generate page tab IDs
  const pageTabIds = quotation.pages.map(page => `page-${page.id}`);

  // Gerar estrutura de navegação baseada em page_groups
  const getNavigationPages = (): any => {
    const pageGroups = (quotation as any)?.page_groups || { groups: [], ungrouped: [] };
    const groups = Array.isArray(pageGroups.groups) ? pageGroups.groups : [];
    const ungrouped = Array.isArray(pageGroups.ungrouped) ? pageGroups.ungrouped : [];
    if (!groups.length && !ungrouped.length) {
      // Fallback: todas as páginas como tabs
      return { linearList: quotation.pages.filter(p => p.isVisible !== false).map(p => ({ type: 'page', page: p })) };
    }
    if (Array.isArray(pageGroups.linearList)) {
      const visiblePages = quotation.pages.filter(p => p.isVisible !== false);
      const linearList = pageGroups.linearList.map(item => {
        if (item.type === 'page') {
          const page = visiblePages.find(p => p.id === item.id);
          return page ? { type: 'page', page } : null;
        } else if (item.type === 'group') {
          const group = groups.find(g => g.id === item.id);
          if (group) {
            const pages = group.pageIds
              .map(pid => visiblePages.find(p => p.id === pid))
              .filter(Boolean);
            if (pages.length > 0) {
              return { type: 'group', group: { id: group.id, name: group.name, pages } };
            }
          }
        }
        return null;
      }).filter(Boolean);
      // Acrescentar páginas visíveis que ainda não estejam representadas
      const representedIds = new Set<string>();
      linearList.forEach((it: any) => {
        if (it.type === 'page') representedIds.add(it.page.id);
        if (it.type === 'group') (it.group.pages || []).forEach((p: any) => representedIds.add(p.id));
      });
      visiblePages.forEach(p => {
        if (!representedIds.has(p.id)) {
          linearList.push({ type: 'page', page: p });
        }
      });
      return { linearList };
    }
    // Fallback: reconstruir a ordem a partir de groups + ungrouped
    const linearList = [
      ...ungrouped.map(id => {
        const page = quotation.pages.find(p => p.id === id && p.isVisible !== false);
        return page ? { type: 'page', page } : null;
      }).filter(Boolean),
      ...groups.map(group => {
        const pages = group.pageIds
          .map(pid => quotation.pages.find(p => p.id === pid && p.isVisible !== false))
          .filter(Boolean);
        if (pages.length > 0) {
          return { type: 'group', group: { id: group.id, name: group.name, pages } };
        }
        return null;
      }).filter(Boolean)
    ];
    // Acrescentar quaisquer páginas visíveis não representadas
    const representedIds = new Set<string>();
    linearList.forEach((it: any) => {
      if (it.type === 'page') representedIds.add(it.page.id);
      if (it.type === 'group') (it.group.pages || []).forEach((p: any) => representedIds.add(p.id));
    });
    quotation.pages.filter(p => p.isVisible !== false).forEach(p => {
      if (!representedIds.has(p.id)) linearList.push({ type: 'page', page: p });
    });
    return { linearList };
  };
  const navigation = getNavigationPages();

  return (
    <div className="min-h-screen bg-voah-cream quotation-view-container overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left side - Full-screen image - Oculto em dispositivos móveis */}
        <div className="hidden lg:block lg:w-1/2 fixed left-0 top-0 bottom-0 h-screen overflow-hidden">
          {/* Background image - now dynamic based on active tab */}
          {activeTab === 'boas-vindas' || !activeTab.startsWith('page-') ? (
          <img 
            src={quotation.coverImages?.[0] || 'https://images.unsplash.com/photo-1506744038136-46273834b3fb'} 
            alt={quotation.title} 
            className="w-full h-full object-cover"
          />
          ) : (
            // Extract page ID from activeTab
            (() => {
              const pageId = activeTab.replace('page-', '');
              const activePage = quotation.pages.find(page => page.id === pageId);
              // Use the page's first image, or fall back to quotation cover image if no page image exists
              return (
                <img 
                  src={
                    (activePage?.images && activePage.images.length > 0) 
                      ? activePage.images[0] 
                      : (quotation.coverImages?.[0] || 'https://images.unsplash.com/photo-1506744038136-46273834b3fb')
                  } 
                  alt={activePage?.title || quotation.title} 
                  className="w-full h-full object-cover"
                />
              );
            })()
          )}
          
          {/* Gradient overlay for better text readability */}
          <div className="absolute top-0 left-0 w-full h-[40%] bg-gradient-to-b from-black/80 to-transparent z-10"></div>
          
          {/* Content aligned to the left at the top */}
          <div className="absolute top-0 left-0 z-20 p-8 lg:p-12 pt-16 lg:pt-20 w-full">
            {activeTab === 'boas-vindas' ? (
              // Conteúdo completo para a aba Boas-vindas
              <>
                <h1 className="text-4xl lg:text-6xl font-bold text-white mb-2 text-left">{quotation.title}</h1>
                <div className="text-white/90 text-xl mb-4 text-left">{formatClientNames(quotation.client)}</div>
                <div className="flex items-center text-white/90 mb-1 text-left">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span>{destinationDisplay}</span>
                </div>
                {coverDateRange && (
                  <div className="flex flex-col gap-2 text-left">
                    <span className="text-sm text-white flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {coverDateRange}
                    </span>
                  </div>
                )}
              </>
            ) : activeTab === 'voos' ? (
              // Título para aba de Voos
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-2 text-left">
                {quotation.flightsTabName || "Voos"}
              </h1>
            ) : activeTab === 'hospedagens' ? (
              // Título para aba de Hospedagens
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-2 text-left">Hospedagens</h1>
            ) : activeTab === 'precos' ? (
              // Título para aba de Preços
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-2 text-left">Preços</h1>
            ) : activeTab === 'recomendacoes' ? (
              // Título para aba de Recomendações
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-2 text-left">
                {quotation.recommendationsTabName || 'Mais Dicas'}
              </h1>
            ) : activeTab.startsWith('page-') ? (
              // Título da página específica para abas de página
              (() => {
                const pageId = activeTab.replace('page-', '');
                const page = quotation.pages.find(p => p.id === pageId);
                return (
                  <h1 className="text-4xl lg:text-6xl font-bold text-white mb-2 text-left">
                    {page?.title || `Página ${page ? quotation.pages.indexOf(page) + 1 : ''}`}
                  </h1>
                );
              })()
            ) : (
              // Fallback para outras abas
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-2 text-left">{quotation.title}</h1>
            )}
          </div>
        </div>
        
        {/* Right side - Scrollable content with fixed menu - Ocupa toda a largura em dispositivos móveis */}
        <div className="w-full lg:w-1/2 lg:ml-[50%] bg-white flex flex-col h-screen overflow-hidden">
          {/* Back button and logo - fixed at top */}
          <div className="fixed top-0 left-0 right-0 lg:left-1/2 lg:right-0 z-30 bg-voah-teal text-white shadow-md">
            <div className="flex items-center justify-between px-4 py-2 border-b-0 border-white/10">
              {/* Mostrar o botão "Voltar" apenas para admins */}
              {isAdmin ? (
              <Button 
                onClick={handleBackNavigation} 
                variant="ghost"
                size="sm"
                className="text-white hover:text-white/80"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar
              </Button>
              ) : (
              /* Se não for admin, colocar um espaço com a mesma altura do botão */
              <div className="h-8 w-8"></div>
              )}
              
              {/* Centered logo */}
              <div className="absolute left-1/2 transform -translate-x-1/2">
                <img 
                  src="/lovable-uploads/3aedb010-4564-4186-8687-ad2d8f6d7160.png" 
                  alt="VOAH Logo" 
                  className="h-8 w-auto"
                />
              </div>
              
              {/* Botões à direita */}
              <div className="flex items-center gap-2">
                {/* Mostrar o botão "PDF" apenas para admins */}
                {isAdmin ? (
                <Button 
                  onClick={() => navigate(`/quotation/${id}/pdf-lite`)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:text-white/80 focus:ring-1 focus:ring-white/30 relative group"
                  title="Visualizar para impressão"
                >
                  <FileTextIcon className="h-4 w-4" />
                  <span className="sr-only">Visualizar para impressão</span>
                  <div className="absolute -bottom-9 right-0 w-40 px-2 py-1 bg-black/80 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    Visualizar para impressão
                  </div>
                </Button>
                ) : (
                /* Se não for admin, colocar um espaço com a mesma altura do botão */
                <div className="h-8 w-8"></div>
                )}
                
                {/* Mostrar o botão "Configurações" apenas para admins */}
                {isAdmin ? (
                <Button 
                  onClick={() => setShowConfigModal(true)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:text-white/80 focus:ring-1 focus:ring-white/30 relative group"
                  title="Configurações de visualização"
                >
                  <Settings className="h-4 w-4" />
                  <span className="sr-only">Configurações de visualização</span>
                  <div className="absolute -bottom-9 right-0 w-40 px-2 py-1 bg-black/80 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    Configurações de visualização
                  </div>
                </Button>
                ) : (
                /* Se não for admin, colocar um espaço com a mesma altura do botão */
                <div className="h-8 w-8"></div>
                )}
              </div>
            </div>
          </div>
          
          {/* Tabs and content */}
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab} 
            className="flex-1 flex flex-col h-full overflow-hidden"
          >
            <div className="fixed top-[44px] left-0 right-0 lg:left-1/2 lg:right-0 z-20 bg-white shadow-sm w-screen lg:w-auto">
              <QuotationPageNavigation
                linearList={navigation.linearList}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                usePageDropdown={navigation.linearList.some(item => item.type === 'group')}
                pageGroups={navigation.linearList.filter(item => item.type === 'group')}
                additionalTabs={[
                  {
                    id: 'hospedagens',
                    label: 'Hospedagens',
                    condition: quotation.showAccommodationsTab !== false,
                    customLabel: quotation.accommodationsTabName || 'Hospedagens'
                  },
                  {
                    id: 'voos',
                    label: 'Voos',
                    condition: quotation.showFlightsTab !== false,
                    customLabel: quotation.flightsTabName || 'Voos'
                  },
                  {
                    id: 'recomendacoes',
                    label: 'Mais Dicas',
                    condition: !!quotation.showRecommendationsTab,
                    customLabel: quotation.recommendationsTabName || 'Mais Dicas'
                  },
                  {
                    id: 'precos',
                    label: 'Preços',
                    condition: !!quotation.showPriceTab,
                    customLabel: quotation.priceTabName || 'Preços'
                  },
                  {
                    id: 'customtab',
                    label: 'Personalizada',
                    condition: !!quotation.showCustomTab,
                    customLabel: quotation.customTabName || 'Personalizada'
                  }
                ]}
              />
            </div>
            
            {/* Scrollable content inside Tabs */}
            <div className="flex-1 pt-[96px] h-[calc(100vh-96px)] overflow-y-auto">
              <TabsContent value="boas-vindas" className="p-0 m-0">
                <WelcomeTab quotation={quotation} coverDateRange={coverDateRange} />
              </TabsContent>
              
              {/* Conteúdo dinâmico para cada página - filter out hidden pages */}
              {quotation.pages.filter(page => page.isVisible !== false).map((page) => (
                <TabsContent 
                  key={`page-${page.id}`} 
                  value={`page-${page.id}`} 
                  className="p-0 m-0"
                >
                  <div className="p-0">
                    {/* Hero image para dispositivos móveis - páginas */}
                    <div className="lg:hidden relative">
                      <div className="h-48 sm:h-56 w-full overflow-hidden">
                        <img 
                          src={
                            (page.images && page.images.length > 0) 
                              ? page.images[0] 
                              : (quotation.coverImages?.[0] || 'https://images.unsplash.com/photo-1506744038136-46273834b3fb')
                          } 
                          alt={page.title || `Página ${quotation.pages.indexOf(page) + 1}`} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                        
                    {/* Descrição da página - agora com colapso */}
                    {page.description && page.description.replace(/<[^>]*>/g, '').trim() !== '' && (
                      <>
                        <div className={`p-3 sm:p-6 ${collapsedDescriptions[`page-${page.id}`] ? 'hidden' : 'block'}`}>
                          <div 
                            className="text-[14px] sm:text-[16px] text-gray-700 leading-loose text-left magazine-content"
                            dangerouslySetInnerHTML={{ 
                              __html: page.description
                                .replace(/style="[^"]*"/g, '')
                                .replace(/style='[^']*'/g, '')
                                .replace(/<p>/g, '<p class="mb-4 px-2 py-1 drop-cap-paragraph">')
                                .replace(/<p class="mb-4 px-2 py-1 drop-cap-paragraph">(\s*)<\/p>/g, '')
                            }}
                          />
                        </div>
                        
                        {/* Botão de colapso e linha de separação após a descrição - visível apenas em desktop */}
                        <div className="hidden lg:flex justify-center items-center border-b border-gray-200 pb-2">
                          <button 
                            onClick={() => toggleDescription(`page-${page.id}`)}
                            className="text-voah-teal hover:text-voah-teal-dark transition-colors"
                            aria-label={collapsedDescriptions[`page-${page.id}`] ? "Expandir descrição" : "Colapsar descrição"}
                          >
                            {collapsedDescriptions[`page-${page.id}`] ? (
                              <ChevronDown className="h-5 w-5" />
                            ) : (
                              <ChevronUp className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        
                        {/* Informações de data - mantidas se necessário */}
                        {(page.startDate || page.endDate) && (
                          <div className="mt-2 flex items-center justify-center text-xs sm:text-sm text-gray-500 mb-2 hidden lg:flex">
                            <Calendar className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                            <span>
                              {formatSafeDate(page.startDate, "dd/MM/yyyy")} 
                              {page.endDate && page.startDate && page.endDate !== page.startDate && (
                                <>
                                  {" - "}
                                  {formatSafeDate(page.endDate, "dd/MM/yyyy")}
                                </>
                              )}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* QuotationStickyScroll para tablet/desktop */}
                    <div className="hidden lg:block">
                    <QuotationStickyScroll 
                      blocks={extractPageData(page).blocks} 
                      tours={extractPageData(page).tours}
                      transportations={extractPageData(page).transportations}
                      restaurants={extractPageData(page).restaurants}
                      accommodations={getAllAccommodations} // Usar TODAS as acomodações da cotação, não apenas da página atual
                      flights={getAllFlights} // Usar TODOS os voos da cotação, não apenas os da página atual
                      flightEvents={extractPageData(page).flightEvents}
                      accommodationEvents={extractPageData(page).accommodationEvents}
                      scrollId={`page-${page.id}`} 
                      showMapLines={quotation.showMapLines !== false}
                      showAccommodations={!!quotation.showAccommodationsInItinerary}
                      showMap={page.showMap !== false}
                      onViewAccommodationDetails={handleViewAccommodationDetails}
                      onViewFlightDetails={handleViewFlightDetails}
                      onScrollStart={() => {
                          // Colapsar a descrição quando o usuário começa a rolar
                          if (!collapsedDescriptions[`page-${page.id}`]) {
                            setCollapsedDescriptions(prev => ({
                            ...prev,
                              [`page-${page.id}`]: true
                          }));
                        }
                      }}
                    />
                    </div>
                    
                    {/* Layout simples para mobile */}
                    <div className="block lg:hidden">
                      {page.showMap !== false && (
                        <>
                          {/* Modal do mapa */}
                          <Dialog open={showMobileMap} onOpenChange={setShowMobileMap}>
                            <DialogContent className="sm:max-w-[90vw] h-[80vh] p-0">
                              <DialogTitle className="sr-only">Mapa do Roteiro</DialogTitle>
                              <DialogDescription className="sr-only">
                                Visualização de mapa com os pontos do seu roteiro de viagem
                              </DialogDescription>
                              <div className="flex-1 h-full">
                                <PageMapMobile 
                                  blocks={extractPageData(page).blocks}
                                  tours={extractPageData(page).tours}
                                  transportations={extractPageData(page).transportations}
                                  restaurants={extractPageData(page).restaurants}
                                  accommodations={getAllAccommodations} // Usar TODAS as acomodações da cotação
                                  flights={getAllFlights} // Agora usando todos os voos da cotação
                                  flightEvents={extractPageData(page).flightEvents}
                                  accommodationEvents={extractPageData(page).accommodationEvents}
                                  showAccommodations={!!quotation.showAccommodationsInItinerary}
                                />
                              </div>
                              
                              {/* Botão de fechar mais visível */}
                              <button 
                                onClick={() => setShowMobileMap(false)}
                                className="absolute top-3 right-3 z-50 bg-[#F97316] text-white rounded-full p-2 shadow-md hover:bg-[#EA580C] transition-colors"
                                aria-label="Fechar mapa"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            </DialogContent>
                          </Dialog>
                        </>
                      )}
                      
                      {/* Conteúdo em formato de lista - agora usando unifiedItems */}
                      <div className="mt-4 px-3 sm:px-6">
                        {page.showMap !== false && (
                          <Button 
                            onClick={() => setShowMobileMap(true)}
                            size="sm"
                            variant="ghost"
                            className="text-voah-coral hover:text-voah-coral-dark mb-4 w-full flex items-center justify-center gap-2 px-4 py-2"
                          >
                            <Map className="h-4 w-4" />
                            <span>Abrir o Mapa</span>
                          </Button>
                        )}
                        
                        {getUnifiedItems({
                          blocks: extractPageData(page).blocks,
                          tours: extractPageData(page).tours,
                          transportations: extractPageData(page).transportations,
                          restaurants: extractPageData(page).restaurants,
                          accommodations: getAllAccommodations, // Agora usando todas as acomodações da cotação
                          flights: getAllFlights, // Agora usando todos os voos da cotação, não apenas os da página atual
                          flightEvents: extractPageData(page).flightEvents,
                          accommodationEvents: extractPageData(page).accommodationEvents,
                          showAccommodations: !!quotation.showAccommodationsInItinerary
                        }).map((item, index) => {
                          // Determine item type and render appropriate card
                          if (item.type === 'block') {
                            return (
                              <div key={item.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                {/* Título do bloco - agora como botão de toggle */}
                                <button 
                                  onClick={() => toggleItem(item.id)}
                                  className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                                >
                                  <h3 className="text-lg font-bold text-voah-teal flex items-center">
                                    <span className="flex items-center justify-center bg-voah-teal text-white rounded-full w-6 h-6 text-xs mr-2">
                                      {index + 1}
                                    </span>
                                    {item.title}
                                  </h3>
                                  <ChevronDown className={`h-5 w-5 text-voah-teal transition-transform duration-200 ${expandedItemId === item.id ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {/* Conteúdo colapsável */}
                                <div className={`transition-all duration-300 overflow-hidden ${
                                  expandedItemId === item.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                                }`}>
                                  {/* Link para Google Maps */}
                                  {createGoogleMapsUrl(item) && (
                                    <div className="px-4 py-2 border-b border-gray-100">
                                      <button 
                                        onClick={() => openInGoogleMaps(item)}
                                        className="flex items-center text-voah-teal text-sm hover:underline cursor-pointer w-full text-left"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver no Google Maps
                                      </button>
                                    </div>
                                  )}
                                  
                                  {/* Imagens do bloco */}
                                  {item.images && item.images.length > 0 && (
                                    <div className="mb-4 -mx-0 overflow-hidden">
                                      <Carousel>
                                        <CarouselContent>
                                          {item.images.map((image, imgIndex) => (
                                            <CarouselItem key={imgIndex}>
                                              <div className="h-48 sm:h-56 overflow-hidden">
                                                <img 
                                                  src={image} 
                                                  alt={`${item.title} - Imagem ${imgIndex + 1}`} 
                                                  className="w-full h-full object-cover"
                                                />
                                              </div>
                                            </CarouselItem>
                                          ))}
                                        </CarouselContent>
                                        <CarouselPrevious className="left-1" />
                                        <CarouselNext className="right-1" />
                                      </Carousel>
                                    </div>
                                  )}
                                  
                                  {/* Texto do bloco */}
                                  {item.description && (
                                    <div 
                                      className="text-[14px] text-gray-700 leading-relaxed p-4 text-left"
                                      dangerouslySetInnerHTML={{ 
                                        __html: item.description
                                          .replace(/style="[^"]*"/g, '')
                                          .replace(/style='[^']*'/g, '')
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          } else if (item.type === 'tour') {
                            return (
                              <div key={item.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                {/* Título do tour - agora como botão de toggle */}
                                <button 
                                  onClick={() => toggleItem(item.id)}
                                  className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                                >
                                  <h3 className="text-lg font-bold text-voah-teal flex items-center">
                                    <span className="flex items-center justify-center bg-voah-teal text-white rounded-full w-6 h-6 text-xs mr-2">
                                      {index + 1}
                                    </span>
                                    {item.name}
                                  </h3>
                                  <ChevronDown className={`h-5 w-5 text-voah-teal transition-transform duration-200 ${expandedItemId === item.id ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {/* Conteúdo colapsável */}
                                <div className={`transition-all duration-300 overflow-hidden ${
                                  expandedItemId === item.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                                }`}>
                                  {/* Link para Google Maps */}
                                  {createGoogleMapsUrl(item) && (
                                    <div className="px-4 py-2 border-b border-gray-100">
                                      <button 
                                        onClick={() => openInGoogleMaps(item)}
                                        className="flex items-center text-voah-teal text-sm hover:underline cursor-pointer w-full text-left"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver no Google Maps
                                      </button>
                                    </div>
                                  )}
                                  
                                  {/* Imagens do tour */}
                                  {item.images && item.images.length > 0 && (
                                    <div className="mb-4 -mx-0 overflow-hidden">
                                      <Carousel>
                                        <CarouselContent>
                                          {item.images.map((image, imgIndex) => (
                                            <CarouselItem key={imgIndex}>
                                              <div className="h-48 sm:h-56 overflow-hidden">
                                                <img 
                                                  src={image} 
                                                  alt={`${item.name} - Imagem ${imgIndex + 1}`} 
                                                  className="w-full h-full object-cover"
                                                />
                                              </div>
                                            </CarouselItem>
                                          ))}
                                        </CarouselContent>
                                        <CarouselPrevious className="left-1" />
                                        <CarouselNext className="right-1" />
                                      </Carousel>
                                    </div>
                                  )}
                                  
                                  {/* Texto do tour */}
                                  {item.description && (
                                    <div 
                                      className="text-[14px] text-gray-700 leading-relaxed p-4 text-left"
                                      dangerouslySetInnerHTML={{ 
                                        __html: item.description
                                          .replace(/style="[^"]*"/g, '')
                                          .replace(/style='[^']*'/g, '')
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          } else if (item.type === 'transportation') {
                            return (
                              <div key={item.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                {/* Título do transportation - agora como botão de toggle */}
                                <button 
                                  onClick={() => toggleItem(item.id)}
                                  className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                                >
                                  <h3 className="text-lg font-bold text-voah-teal flex items-center">
                                    <span className="flex items-center justify-center bg-voah-teal text-white rounded-full w-6 h-6 text-xs mr-2">
                                      {index + 1}
                                    </span>
                                    {item.name}
                                  </h3>
                                  <ChevronDown className={`h-5 w-5 text-voah-teal transition-transform duration-200 ${expandedItemId === item.id ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {/* Conteúdo colapsável */}
                                <div className={`transition-all duration-300 overflow-hidden ${
                                  expandedItemId === item.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                                }`}>
                                  {/* Links para Google Maps - Podemos ter partida e chegada */}
                                  {(item.departureGoogleMapsUrl || item.departurePlaceId || item.departureCoordinates) && (
                                    <div className="px-4 py-2 border-b border-gray-100">
                                      <button 
                                        onClick={() => openInGoogleMaps({...item, googleMapsUrl: item.departureGoogleMapsUrl, placeId: item.departurePlaceId, coordinates: item.departureCoordinates})}
                                        className="flex items-center text-voah-teal text-sm hover:underline cursor-pointer w-full text-left"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver local de partida no Google Maps
                                      </button>
                                    </div>
                                  )}
                                  
                                  {(item.arrivalGoogleMapsUrl || item.arrivalPlaceId || item.arrivalCoordinates) && (
                                    <div className="px-4 py-2 border-b border-gray-100">
                                      <button 
                                        onClick={() => openInGoogleMaps({...item, googleMapsUrl: item.arrivalGoogleMapsUrl, placeId: item.arrivalPlaceId, coordinates: item.arrivalCoordinates})}
                                        className="flex items-center text-voah-teal text-sm hover:underline cursor-pointer w-full text-left"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver local de chegada no Google Maps
                                      </button>
                                    </div>
                                  )}
                                  
                                  {/* Link genérico caso não tenha específicos de partida/chegada */}
                                  {!((item.departureGoogleMapsUrl || item.departurePlaceId || item.departureCoordinates) || 
                                     (item.arrivalGoogleMapsUrl || item.arrivalPlaceId || item.arrivalCoordinates)) && 
                                    createGoogleMapsUrl(item) && (
                                    <div className="px-4 py-2 border-b border-gray-100">
                                      <button 
                                        onClick={() => openInGoogleMaps(item)}
                                        className="flex items-center text-voah-teal text-sm hover:underline cursor-pointer w-full text-left"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver no Google Maps
                                      </button>
                                    </div>
                                  )}
                                  
                                  {/* Imagens do transportation */}
                                  {item.images && item.images.length > 0 && (
                                    <div className="mb-4 -mx-0 overflow-hidden">
                                      <Carousel>
                                        <CarouselContent>
                                          {item.images.map((image, imgIndex) => (
                                            <CarouselItem key={imgIndex}>
                                              <div className="h-48 sm:h-56 overflow-hidden">
                                                <img 
                                                  src={image} 
                                                  alt={`${item.name} - Imagem ${imgIndex + 1}`} 
                                                  className="w-full h-full object-cover"
                                                />
                                              </div>
                                            </CarouselItem>
                                          ))}
                                        </CarouselContent>
                                        <CarouselPrevious className="left-1" />
                                        <CarouselNext className="right-1" />
                                      </Carousel>
                                    </div>
                                  )}

                                  {/* Informações de origem e destino */}
                                  {(item.departureLocation || item.arrivalLocation) && (
                                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {item.departureLocation && (
                                          <div className="flex items-center">
                                            <div className="mr-2 text-voah-teal">
                                              <MapPin className="h-4 w-4" />
                                            </div>
                                            <div>
                                              <span className="text-xs text-gray-500">Origem</span>
                                              <div className="text-sm font-medium">{item.departureLocation}</div>
                                            </div>
                                          </div>
                                        )}
                                        {item.arrivalLocation && (
                                          <div className="flex items-center">
                                            <div className="mr-2 text-voah-teal">
                                              <MapPin className="h-4 w-4" />
                                            </div>
                                            <div>
                                              <span className="text-xs text-gray-500">Destino</span>
                                              <div className="text-sm font-medium">{item.arrivalLocation}</div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Data e Hora */}
                                  {(item.date || item.time || item.duration) && (
                                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {item.time && (
                                          <div className="flex items-center">
                                            <div className="mr-2 text-voah-teal">
                                              <Clock className="h-4 w-4" />
                                            </div>
                                            <div>
                                              <span className="text-xs text-gray-500">Horário</span>
                                              <div className="text-sm font-medium">{item.time}</div>
                                            </div>
                                          </div>
                                        )}
                                        {item.duration && (
                                          <div className="flex items-center">
                                            <div className="mr-2 text-voah-teal">
                                              <Clock className="h-4 w-4" />
                                            </div>
                                            <div>
                                              <span className="text-xs text-gray-500">Duração</span>
                                              <div className="text-sm font-medium">{item.duration}</div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Texto do transportation */}
                                  {item.description && (
                                    <div 
                                      className="text-[14px] text-gray-700 leading-relaxed p-4 text-left"
                                      dangerouslySetInnerHTML={{ 
                                        __html: item.description
                                          .replace(/style="[^"]*"/g, '')
                                          .replace(/style='[^']*'/g, '')
                                      }}
                                    />
                                  )}
                                  
                                  {/* Informações Importantes */}
                                  {item.importantInfo && item.importantInfo.trim() !== '' && (
                                    <div className="mx-4 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                                      <div className="flex items-start gap-2">
                                        <Info className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                        <div className="text-left">
                                          <div className="text-sm font-medium text-gray-800 mb-1">Informações Importantes:</div>
                                          <div className="text-[15px] text-gray-700">{item.importantInfo}</div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          } else if (item.type === 'accommodation' || item.type === 'accommodation-checkin' || item.type === 'accommodation-checkout') {
                            // Get original ID for accommodations to ensure correct handling of check-in/check-out events
                            const originalId = item.id.includes('-') ? item.id.split('-')[0] : item.id;
                            const isCheckIn = item.type === 'accommodation-checkin';
                            const isCheckOut = item.type === 'accommodation-checkout';
                            
                            // Cast item para AccommodationInfo para acessar as propriedades corretamente
                            const accommodationItem = item as unknown as AccommodationInfo;
                            
                            // NOVO: fallback para eventos sem parent
                            const eventDate = (isCheckIn || isCheckOut) ? (item.date || (isCheckIn ? accommodationItem.checkInDate : accommodationItem.checkOutDate)) : undefined;
                            const eventTime = (isCheckIn || isCheckOut) ? (item.time || (isCheckIn ? accommodationItem.checkInTime : accommodationItem.checkOutTime)) : undefined;
                            
                            return (
                              <div key={item.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                {/* Título da hospedagem - com indicação de check-in/check-out quando aplicável */}
                                <button 
                                  onClick={() => toggleItem(item.id)}
                                  className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                                >
                                  <h3 className="text-lg font-bold text-voah-teal flex items-center">
                                    <span className={`flex items-center justify-center ${
                                      isCheckIn ? 'bg-green-600' : 
                                      isCheckOut ? 'bg-red-600' : 
                                      'bg-voah-teal'
                                    } text-white rounded-full w-6 h-6 text-xs mr-2`}>
                                      {index + 1}
                                    </span>
                                    {isCheckIn && "Check-in: "}
                                    {isCheckOut && "Check-out: "}
                                    {accommodationItem.name}
                                  </h3>
                                  <ChevronDown className={`h-5 w-5 text-voah-teal transition-transform duration-200 ${expandedItemId === item.id ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {/* Conteúdo colapsável */}
                                <div className={`transition-all duration-300 overflow-hidden ${
                                  expandedItemId === item.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                                }`}>
                                  {/* Link para Google Maps */}
                                  {createGoogleMapsUrl(accommodationItem) && (
                                    <div className="px-4 py-2 border-b border-gray-100">
                                      <button 
                                        onClick={() => openInGoogleMaps(accommodationItem)}
                                        className="flex items-center text-voah-teal text-sm hover:underline cursor-pointer w-full text-left"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver no Google Maps
                                      </button>
                                    </div>
                                  )}
                                  
                                  {/* Imagens da hospedagem */}
                                  {accommodationItem.images && accommodationItem.images.length > 0 && (
                                    <div className="mb-4 -mx-0 overflow-hidden">
                                      <Carousel>
                                        <CarouselContent>
                                          {accommodationItem.images.map((image, imgIndex) => (
                                            <CarouselItem key={imgIndex}>
                                              <div className="h-48 sm:h-56 overflow-hidden">
                                                <img 
                                                  src={image} 
                                                  alt={`${accommodationItem.name} - Imagem ${imgIndex + 1}`} 
                                                  className="w-full h-full object-cover"
                                                />
                                              </div>
                                            </CarouselItem>
                                          ))}
                                        </CarouselContent>
                                        <CarouselPrevious className="left-1" />
                                        <CarouselNext className="right-1" />
                                      </Carousel>
                                    </div>
                                  )}

                                  {/* Informações da hospedagem */}
                                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      {/* Localização */}
                                      {accommodationItem.location && (
                                        <div className="flex items-center">
                                          <div className="mr-2 text-voah-teal">
                                            <MapPin className="h-4 w-4" />
                                          </div>
                                          <div>
                                            <span className="text-xs text-gray-500">Localização</span>
                                            <div className="text-sm font-medium">{accommodationItem.location}</div>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Check-in e Check-out para acomodação regular */}
                                      {!isCheckIn && !isCheckOut && accommodationItem.checkInTime && (
                                        <div className="flex items-center">
                                          <div className="mr-2 text-green-600">
                                            <LogIn className="h-4 w-4" />
                                          </div>
                                          <div>
                                            <span className="text-xs text-gray-500">Check-in</span>
                                            <div className="text-sm font-medium">
                                              {accommodationItem.checkInDate && `${accommodationItem.checkInDate} `}
                                              {accommodationItem.checkInTime && `${accommodationItem.checkInTime}`}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {!isCheckIn && !isCheckOut && accommodationItem.checkOutTime && (
                                        <div className="flex items-center">
                                          <div className="mr-2 text-red-600">
                                            <LogOut className="h-4 w-4" />
                                          </div>
                                          <div>
                                            <span className="text-xs text-gray-500">Check-out</span>
                                            <div className="text-sm font-medium">
                                              {accommodationItem.checkOutDate && `${accommodationItem.checkOutDate} `}
                                              {accommodationItem.checkOutTime && `${accommodationItem.checkOutTime}`}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Horário para eventos específicos de check-in/check-out */}
                                      {isCheckIn && (eventDate || eventTime) && (
                                        <div className="flex items-center">
                                          <div className="mr-2 text-green-600">
                                            <Clock className="h-4 w-4" />
                                          </div>
                                          <div>
                                            <span className="text-xs text-gray-500">Check-in</span>
                                            <div className="text-sm font-medium">
                                              {eventDate && `${formatDateBR(eventDate)} `}
                                              {eventTime && `${eventTime}`}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      {isCheckOut && (eventDate || eventTime) && (
                                        <div className="flex items-center">
                                          <div className="mr-2 text-red-600">
                                            <Clock className="h-4 w-4" />
                                          </div>
                                          <div>
                                            <span className="text-xs text-gray-500">Check-out</span>
                                            <div className="text-sm font-medium">
                                              {eventDate && `${formatDateBR(eventDate)} `}
                                              {eventTime && `${eventTime}`}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Tipo de quarto */}
                                      {accommodationItem.roomType && (
                                        <div className="flex items-center">
                                          <div className="mr-2 text-voah-teal">
                                            <Bed className="h-4 w-4" />
                                          </div>
                                          <div>
                                            <span className="text-xs text-gray-500">Tipo de Quarto</span>
                                            <div className="text-sm font-medium">{accommodationItem.roomType}</div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Descrição da hospedagem */}
                                  {accommodationItem.description && (
                                    <div 
                                      className="text-[14px] text-gray-700 leading-relaxed p-4 text-left"
                                      dangerouslySetInnerHTML={{ 
                                        __html: accommodationItem.description
                                          .replace(/style="[^"]*"/g, '')
                                          .replace(/style='[^']*'/g, '')
                                      }}
                                    />
                                  )}
                                  
                                  {/* Botão para ver detalhes completos */}
                                  <div className="p-4 flex justify-end">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="border-voah-teal text-voah-teal hover:bg-voah-teal hover:text-white"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewAccommodationDetails(originalId);
                                      }}
                                    >
                                      <Eye className="h-4 w-4 mr-1" />
                                      Ver Detalhes
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          } else if (item.type === 'restaurant') {
                            return (
                              <div key={item.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                {/* Título do restaurante */}
                                <button 
                                  onClick={() => toggleItem(item.id)}
                                  className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                                >
                                  <h3 className="text-lg font-bold text-voah-teal flex items-center">
                                    <span className="flex items-center justify-center bg-voah-teal text-white rounded-full w-6 h-6 text-xs mr-2">
                                      {index + 1}
                                    </span>
                                    {item.name}
                                  </h3>
                                  <ChevronDown className={`h-5 w-5 text-voah-teal transition-transform duration-200 ${expandedItemId === item.id ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {/* Conteúdo colapsável */}
                                <div className={`transition-all duration-300 overflow-hidden ${
                                  expandedItemId === item.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                                }`}>
                                  {/* Link para Google Maps */}
                                  {createGoogleMapsUrl(item) && (
                                    <div className="px-4 py-2 border-b border-gray-100">
                                      <button 
                                        onClick={() => openInGoogleMaps(item)}
                                        className="flex items-center text-voah-teal text-sm hover:underline cursor-pointer w-full text-left"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver no Google Maps
                                      </button>
                                    </div>
                                  )}
                                  
                                  {/* Imagens do restaurante */}
                                  {item.images && item.images.length > 0 && (
                                    <div className="mb-4 -mx-0 overflow-hidden">
                                      <Carousel>
                                        <CarouselContent>
                                          {item.images.map((image, imgIndex) => (
                                            <CarouselItem key={imgIndex}>
                                              <div className="h-48 sm:h-56 overflow-hidden">
                                                <img 
                                                  src={image} 
                                                  alt={`${item.name} - Imagem ${imgIndex + 1}`} 
                                                  className="w-full h-full object-cover"
                                                />
                                              </div>
                                            </CarouselItem>
                                          ))}
                                        </CarouselContent>
                                        <CarouselPrevious className="left-1" />
                                        <CarouselNext className="right-1" />
                                      </Carousel>
                                    </div>
                                  )}
                                  
                                  {/* Informações do local */}
                                  {(item.location || item.date || item.time) && (
                                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                                      <div className="grid grid-cols-1 gap-2">
                                        {item.location && (
                                          <div className="flex items-center">
                                            <div className="mr-2 text-voah-teal">
                                              <MapPin className="h-4 w-4" />
                                            </div>
                                            <div>
                                              <span className="text-xs text-gray-500">Local</span>
                                              <div className="text-sm font-medium">{item.location}</div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Detalhes sobre culinária e destaques do menu */}
                                  {(item.cuisineType || item.menuHighlights) && (
                                    <div className="px-4 py-3 border-b border-gray-200">
                                      {item.cuisineType && (
                                        <div className="mb-2">
                                          <div className="text-xs text-gray-500">Tipo de Culinária</div>
                                          <div className="text-sm font-medium">{item.cuisineType}</div>
                                        </div>
                                      )}
                                      {item.menuHighlights && (
                                        <div>
                                          <div className="text-xs text-gray-500">Destaques do Menu</div>
                                          <div className="text-sm font-medium">{item.menuHighlights}</div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Texto do restaurante */}
                                  {item.description && (
                                    <div 
                                      className="text-[14px] text-gray-700 leading-relaxed p-4 text-left"
                                      dangerouslySetInnerHTML={{ 
                                        __html: item.description
                                          .replace(/style="[^"]*"/g, '')
                                          .replace(/style='[^']*'/g, '')
                                      }}
                                    />
                                  )}
                                  
                                  {/* Observações - exibir da mesma forma que Informações Importantes dos transportes */}
                                  {item.notes && item.notes.trim() !== '' && (
                                    <div className="mx-4 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                                      <div className="flex items-start gap-2">
                                        <Info className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                        <div className="text-left">
                                          <div className="text-sm font-medium text-gray-800 mb-1">Observações:</div>
                                          <div className="text-[15px] text-gray-700">{item.notes}</div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          } else if (item.type === 'flight' || item.type === 'flight-departure' || item.type === 'flight-arrival') {
                            // Determinar se é partida, chegada ou voo completo
                            const isFlight = item.type === 'flight';
                            const isDeparture = item.type === 'flight-departure';
                            const isArrival = item.type === 'flight-arrival';
                            
                            // Log para depurar o problema do horário
                            console.log("FlightEvent Debug:", {
                              id: item.id,
                              type: item.type,
                              flightId: item.flightId,
                              departureDateTime: item.departureDateTime,
                              arrivalDateTime: item.arrivalDateTime,
                              connections: item.connections
                            });
                            
                            // Extrair horários diretamente das strings ISO
                            const getTimeFromISOString = (isoString: string | undefined) => {
                              if (!isoString) return '';
                              return isoString.includes('T') ? isoString.split('T')[1].substring(0, 5) : formatSafeDate(isoString, 'HH:mm');
                            };
                            
                            // Definir o título com base no tipo
                            const flightTitle = isFlight 
                              ? `${item.airline || ''} ${item.flightNumber || ''} - ${item.departureAirport || ''} → ${item.arrivalAirport || ''}` 
                              : isDeparture 
                                ? `Partida: ${item.airline || ''} ${item.flightNumber || ''} - ${item.departureAirport || ''}` 
                                : `Chegada: ${item.airline || ''} ${item.flightNumber || ''} - ${item.arrivalAirport || ''}`;

                            return (
                              <div key={item.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                {/* Título do voo como botão de toggle */}
                                <button 
                                  onClick={() => toggleItem(item.id)}
                                  className="w-full px-4 py-3 bg-blue-50 text-blue-700 flex items-center justify-between text-left hover:bg-blue-100 transition-colors"
                                >
                                  <h3 className="text-lg font-bold flex items-center">
                                    <span className="flex items-center justify-center bg-blue-100 text-blue-700 rounded-full w-6 h-6 text-xs mr-2">
                                      {index + 1}
                                    </span>
                                    {isDeparture && (
                                      <div className="flex flex-col">
                                        <span className="text-sm font-medium">Partida</span>
                                        <span>
                                          {item.departureAirport}
                                          {item.departureDateTime && (
                                            <span className="text-xs text-gray-500 ml-2">
                                              {formatSafeDate(item.departureDateTime, "dd/MM/yyyy")} • {getTimeFromISOString(item.departureDateTime)}
                                            </span>
                                          )}
                                        </span>
                                        <div className="text-xs text-voah-teal mt-1">
                                          {item.airline || ''} {item.flightNumber || ''}
                                        </div>
                                      </div>
                                    )}
                                    {isArrival && (
                                      <div className="flex flex-col">
                                        <span className="text-sm font-medium">Chegada</span>
                                        <span>
                                          {item.connections && item.connections.length > 0
                                            ? item.connections[item.connections.length - 1].arrivalAirport
                                            : item.arrivalAirport}
                                            {item.connections && item.connections.length > 0
                                            ? (item.connections[item.connections.length - 1].arrivalDateTime && (
                                                <span className="text-xs text-gray-500 ml-2">
                                                  {formatSafeDate(item.connections[item.connections.length - 1].arrivalDateTime, "dd/MM/yyyy")} • {getTimeFromISOString(item.connections[item.connections.length - 1].arrivalDateTime)}
                                          </span>
                                              ))
                                            : (item.arrivalDateTime && (
                                                <span className="text-xs text-gray-500 ml-2">
                                                  {formatSafeDate(item.arrivalDateTime, "dd/MM/yyyy")} • {getTimeFromISOString(item.arrivalDateTime)}
                                                </span>
                                              ))}
                                        </span>
                                        <div className="text-xs text-voah-teal mt-1">
                                          {item.connections && item.connections.length > 0
                                            ? `${item.connections[item.connections.length - 1].airline || ''} ${item.connections[item.connections.length - 1].flightNumber || ''}`
                                            : `${item.airline || ''} ${item.flightNumber || ''}`}
                                        </div>
                                      </div>
                                    )}
                                    {isFlight && !isDeparture && !isArrival && (
                                      <div className="flex flex-col">
                                        <span className="text-sm font-medium">Voo</span>
                                        <span>{item.departureAirport} → {item.arrivalAirport}</span>
                                        <div className="text-xs text-voah-teal mt-1">
                                          {item.airline || ''} {item.flightNumber || ''}
                                        </div>
                                      </div>
                                    )}
                                  </h3>
                                  <ChevronDown className={`h-5 w-5 text-blue-500 transition-transform duration-200 ${expandedItemId === item.id ? 'rotate-180' : ''}`} />
                                </button>
                                
                                <div className={`transition-all duration-300 overflow-hidden ${
                                  expandedItemId === item.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                                }`}>
                                  {/* Detalhes do voo com card estilizado */}
                                  <div className="p-4">
                                    {/* Card de voo estilizado, igual ao da aba de voos */}
                                    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                                      {/* Header do voo */}
                                      <div className="bg-voah-teal text-white p-4">
                                        <div className="flex flex-col sm:flex-row items-center justify-between">
                                          <div className="w-full sm:w-auto mb-3 sm:mb-0">
                                            <div className="flex items-center justify-center sm:justify-start">
                                              <PlaneTakeoff className="h-6 w-6 mr-3" />
                                              <h3 className="text-xl font-semibold text-center sm:text-left">
                                                {isDeparture 
                                                  ? (item.flightNumber ? `Voo ${item.airline} ${item.flightNumber}` : (item.airline || 'Voo')) 
                                                  : isArrival && item.connections && item.connections.length > 0
                                                  ? `Voo ${item.connections[item.connections.length - 1].airline} ${item.connections[item.connections.length - 1].flightNumber}`
                                                  : (item.flightNumber ? `Voo ${item.airline} ${item.flightNumber}` : (item.airline || 'Voo'))
                                                }
                                              </h3>
                                            </div>
                                            <p className="text-white/80 text-sm text-center sm:text-left mt-1">
                                              {isDeparture ? 'Embarque' : isArrival ? 'Desembarque' : 'Voo completo'}
                                            </p>
                                          </div>
                                          <div className="text-center sm:text-right">
                                            {item.price && (
                                              <div className="text-lg font-medium">
                                                {item.price.toLocaleString('pt-BR', { 
                                                  style: 'currency', 
                                                  currency: item.currency || 'BRL' 
                                                })}
                                      </div>
                                    )}
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Flight route visualization */}
                                      <div className="p-6">
                                        <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between mb-6">
                                          {/* Departure information */}
                                          {(isDeparture || isFlight || isArrival) && (
                                            <div className="text-center sm:text-left mb-4 sm:mb-0 sm:w-1/3">
                                              <div className="text-sm text-gray-500 mb-1">Partida</div>
                                              <div className="text-2xl font-bold text-voah-teal">
                                                {(() => {
                                                  // For FlightEvents, try to get departure info from parent flight
                                                  if (isArrival && item.flightId) {
                                                    const flight = extractPageData(page).flights.find(f => f.id === item.flightId);
                                                    if (flight) {
                                                      return flight.departureAirport;
                                                    }
                                                  }
                                                  return item.departureAirport;
                                                })()}
                                              </div>
                                              {(() => {
                                                // For arrival FlightEvents, show departure datetime from parent flight
                                                if (isArrival && item.flightId) {
                                                  const flight = extractPageData(page).flights.find(f => f.id === item.flightId);
                                                  if (flight && flight.departureDateTime) {
                                                    return (
                                                      <div className="text-sm text-gray-700">
                                                        {formatSafeDate(flight.departureDateTime, "dd/MM/yyyy")}
                                                        <span className="mx-1">•</span>
                                                        {getTimeFromISOString(flight.departureDateTime)}
                                                      </div>
                                                    );
                                                  }
                                                } else if (item.departureDateTime) {
                                                  return (
                                                    <div className="text-sm text-gray-700">
                                                      {formatSafeDate(item.departureDateTime, "dd/MM/yyyy")}
                                                      <span className="mx-1">•</span>
                                                      {getTimeFromISOString(item.departureDateTime)}
                                                    </div>
                                                  );
                                                }
                                                return null;
                                              })()}
                                              {(() => {
                                                // For arrival FlightEvents, show departure terminal from parent flight
                                                if (isArrival && item.flightId) {
                                                  const flight = extractPageData(page).flights.find(f => f.id === item.flightId);
                                                  if (flight && flight.departureTerminal) {
                                                    return (
                                                      <div className="text-xs text-gray-500 mt-1">
                                                        Terminal {flight.departureTerminal}
                                                      </div>
                                                    );
                                                  }
                                                } else if (item.departureTerminal) {
                                                  return (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                      Terminal {item.departureTerminal}
                                                    </div>
                                                  );
                                                }
                                                return null;
                                              })()}
                                            </div>
                                          )}
                                          {/* Flight path visualization */}
                                          <div className="flex flex-col items-center justify-center py-2 mb-1 sm:mb-0 sm:w-1/3">
                                            <div className="relative w-full flex items-center justify-center">
                                              <div className="h-0.5 bg-gray-300 w-full absolute"></div>
                                              <ArrowRight className="text-voah-teal z-10 h-6 w-6 bg-white rounded-full p-0.5 shadow-sm" />
                                            </div>
                                            <div className="text-sm text-gray-700 mt-2 mb-1">
                                              <span className="font-medium">Duração: </span>
                                              {(() => {
                                                // Calcular a duração real do voo
                                                if (item.departureDateTime && item.arrivalDateTime) {
                                                  try {
                                                    const departure = parseISO(item.departureDateTime);
                                                    const arrival = parseISO(item.arrivalDateTime);
                                                    const durationMinutes = differenceInMinutes(arrival, departure);
                                                    const hours = Math.floor(durationMinutes / 60);
                                                    const minutes = durationMinutes % 60;
                                                    return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
                                                  } catch (e) {
                                                    return "Duração indisponível";
                                                  }
                                                }
                                                
                                                // Se for um FlightEvent com flightId
                                                if (item.type === 'flight-departure' || item.type === 'flight-arrival') {
                                                  if (item.flightId) {
                                                    const flight = extractPageData(page).flights.find(f => f.id === item.flightId);
                                                    
                                                    if (flight && flight.departureDateTime && flight.arrivalDateTime) {
                                                      try {
                                                        const departure = parseISO(flight.departureDateTime);
                                                        const arrival = parseISO(flight.arrivalDateTime);
                                                        const durationMinutes = differenceInMinutes(arrival, departure);
                                                        const hours = Math.floor(durationMinutes / 60);
                                                        const minutes = durationMinutes % 60;
                                                        return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
                                                      } catch (e) {
                                                        return "Duração indisponível";
                                                      }
                                                    }
                                                  }
                                                }
                                                
                                                return "Duração indisponível";
                                              })()}
                                            </div>
                                            {/* Remoção da rota do voo aqui */}
                                          </div>
                                          
                                          {/* Arrival information - desktop version */}
                                          {(isArrival || isFlight) && (
                                            <div className="text-center sm:text-right sm:w-1/3 sm:block hidden">
                                              <div className="text-sm text-gray-500 mb-1">Chegada</div>
                                              <div className="text-2xl font-bold text-voah-teal">
                                                {item.type === 'flight-arrival' && item.connections && item.connections.length > 0
                                                  ? item.connections[item.connections.length - 1].arrivalAirport
                                                  : item.arrivalAirport}
                                              </div>
                                              {item.type === 'flight-arrival' && item.connections && item.connections.length > 0
                                                ? (item.connections[item.connections.length - 1].arrivalDateTime && (
                                                    <div className="text-sm text-gray-700">
                                                      {formatSafeDate(item.connections[item.connections.length - 1].arrivalDateTime, "dd/MM/yyyy")}
                                                      <span className="mx-1">•</span>
                                                      {getTimeFromISOString(item.connections[item.connections.length - 1].arrivalDateTime)}
                                                    </div>
                                                  ))
                                                : (item.arrivalDateTime && (
                                                    <div className="text-sm text-gray-700">
                                                      {formatSafeDate(item.arrivalDateTime, "dd/MM/yyyy")}
                                                      <span className="mx-1">•</span>
                                                      {getTimeFromISOString(item.arrivalDateTime)}
                                                    </div>
                                                  ))}
                                              {item.type === 'flight-arrival' && item.connections && item.connections.length > 0
                                                ? (item.connections[item.connections.length - 1].arrivalTerminal && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                      Terminal {item.connections[item.connections.length - 1].arrivalTerminal}
                                                    </div>
                                                  ))
                                                : (item.arrivalTerminal && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                      Terminal {item.arrivalTerminal}
                                                    </div>
                                                  ))}
                                      </div>
                                    )}
                                        </div>
                                        
                                        {/* Mobile-specific Arrival section below the divider */}
                                        {(isArrival || isFlight || isDeparture) && (
                                          <div className="sm:hidden block mt-1 pt-2 border-t border-gray-200">
                                            <div className="text-center">
                                              <div className="text-sm text-gray-500 mb-1">Chegada</div>
                                              <div className="text-2xl font-bold text-voah-teal">
                                                {(() => {
                                                  // Verificar se é um FlightEvent com flightId
                                                  if (item.type === 'flight-departure' || item.type === 'flight-arrival') {
                                                    if (item.flightId) {
                                                      const flight = extractPageData(page).flights.find(f => f.id === item.flightId);
                                                      
                                                      if (flight) {
                                                        // Para chegadas com conexões, usar informação da última conexão
                                                        if (flight.connections && flight.connections.length > 0) {
                                                          const lastConnection = flight.connections[flight.connections.length - 1];
                                                          return lastConnection.arrivalAirport || flight.arrivalAirport;
                                                        } else {
                                                          return flight.arrivalAirport;
                                                        }
                                                      }
                                                    }
                                                  }
                                                  
                                                  // Lógica original como fallback
                                                  return item.type === 'flight-arrival' && item.connections && item.connections.length > 0
                                                    ? item.connections[item.connections.length - 1].arrivalAirport
                                                    : item.arrivalAirport;
                                                })()}
                                      </div>
                                              {(() => {
                                                // Verificar se é um FlightEvent com flightId
                                                if (item.type === 'flight-departure' || item.type === 'flight-arrival') {
                                                  if (item.flightId) {
                                                    const flight = extractPageData(page).flights.find(f => f.id === item.flightId);
                                                    
                                                    if (flight) {
                                                      // Para chegadas com conexões, usar data/hora da última conexão
                                                      if (flight.connections && flight.connections.length > 0) {
                                                        const lastConnection = flight.connections[flight.connections.length - 1];
                                                        if (lastConnection.arrivalDateTime) {
                                                          return (
                                                            <div className="text-sm text-gray-700">
                                                              {formatSafeDate(lastConnection.arrivalDateTime, "dd/MM/yyyy")}
                                                              <span className="mx-1">•</span>
                                                              {getTimeFromISOString(lastConnection.arrivalDateTime)}
                                                            </div>
                                                          );
                                                        }
                                                      } else if (flight.arrivalDateTime) {
                                                        // Para voos sem conexões
                                                        return (
                                                          <div className="text-sm text-gray-700">
                                                            {formatSafeDate(flight.arrivalDateTime, "dd/MM/yyyy")}
                                                            <span className="mx-1">•</span>
                                                            {getTimeFromISOString(flight.arrivalDateTime)}
                                                          </div>
                                                        );
                                                      }
                                                    }
                                                  }
                                                }
                                                
                                                // Lógica original como fallback
                                                return item.type === 'flight-arrival' && item.connections && item.connections.length > 0
                                                  ? (item.connections[item.connections.length - 1].arrivalDateTime && (
                                                      <div className="text-sm text-gray-700">
                                                        {formatSafeDate(item.connections[item.connections.length - 1].arrivalDateTime, "dd/MM/yyyy")}
                                                        <span className="mx-1">•</span>
                                                        {getTimeFromISOString(item.connections[item.connections.length - 1].arrivalDateTime)}
                                                      </div>
                                                    ))
                                                  : (item.arrivalDateTime && (
                                                      <div className="text-sm text-gray-700">
                                                        {formatSafeDate(item.arrivalDateTime, "dd/MM/yyyy")}
                                                        <span className="mx-1">•</span>
                                                        {getTimeFromISOString(item.arrivalDateTime)}
                                                      </div>
                                                    ));
                                              })()}
                                              {(() => {
                                                // Verificar se é um FlightEvent com flightId
                                                if (item.type === 'flight-departure' || item.type === 'flight-arrival') {
                                                  if (item.flightId) {
                                                    const flight = extractPageData(page).flights.find(f => f.id === item.flightId);
                                                    
                                                    if (flight) {
                                                      // Para chegadas com conexões, usar informação do terminal da última conexão
                                                      if (flight.connections && flight.connections.length > 0) {
                                                        const lastConnection = flight.connections[flight.connections.length - 1];
                                                        if (lastConnection.arrivalTerminal) {
                                                          return (
                                                            <div className="text-xs text-gray-500 mt-1">
                                                              Terminal {lastConnection.arrivalTerminal}
                                                            </div>
                                                          );
                                                        }
                                                      } else if (flight.arrivalTerminal) {
                                                        // Para voos sem conexões
                                                        return (
                                                          <div className="text-xs text-gray-500 mt-1">
                                                            Terminal {flight.arrivalTerminal}
                                                          </div>
                                                        );
                                                      }
                                                    }
                                                  }
                                                }
                                                
                                                // Lógica original como fallback
                                                return item.type === 'flight-arrival' && item.connections && item.connections.length > 0
                                                  ? (item.connections[item.connections.length - 1].arrivalTerminal && (
                                                      <div className="text-xs text-gray-500 mt-1">
                                                        Terminal {item.connections[item.connections.length - 1].arrivalTerminal}
                                                      </div>
                                                    ))
                                                  : (item.arrivalTerminal && (
                                                      <div className="text-xs text-gray-500 mt-1">
                                                        Terminal {item.arrivalTerminal}
                                                      </div>
                                                    ));
                                              })()}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Botão para visualizar detalhes completos */}
                                    {handleViewFlightDetails && (
                                      <div className="mt-4 flex justify-end">
                                        <Button 
                                          variant="outline"
                                          size="sm"
                                          className="text-blue-700 border-blue-300 hover:bg-blue-50"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            // Check if this is a FlightEvent with a parent flight
                                            if ((item.type === 'flight-departure' || item.type === 'flight-arrival') && item.flightId) {
                                              handleViewFlightDetails(item.flightId);
                                            } else {
                                              // Fallback to the original logic for regular flights
                                              handleViewFlightDetails(item.id.replace('_departure', '').replace('_arrival', ''));
                                            }
                                          }}
                                        >
                                          Ver detalhes completos
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          // Fall back to a default item if type not recognized
                          return null;
                        })}
                      </div>
                    </div>
                  </div>
                </TabsContent>
              ))}
              
              {/* Conteúdo da aba de Hospedagens - exibido apenas quando showAccommodationsTab não for false */}
              {quotation.showAccommodationsTab !== false && (
                <TabsContent value="hospedagens" className="p-0 m-0">
                  <AccommodationsTab quotation={quotation} />
                </TabsContent>
              )}
              
              {/* Conteúdo da aba de Voos - exibido apenas quando showFlightsTab não for false */}
              {quotation.showFlightsTab !== false && (
                <TabsContent value="voos" className="p-0 m-0">
                  <FlightsTab quotation={quotation} />
                </TabsContent>
              )}
              
              {/* Conteúdo da aba de Preços */}
              {quotation.showPriceTab && (
                <TabsContent value="precos" className="p-0 m-0">
                  <PriceTab quotation={quotation} />
                </TabsContent>
              )}
              
              {/* Tab Personalizada */}
              {quotation.showCustomTab && (
                <TabsContent value="customtab" className="p-0 m-0">
                  <CustomTabView quotation={quotation} />
                </TabsContent>
              )}
              
              {/* Tab de Recomendações */}
              {quotation.showRecommendationsTab && (
                <TabsContent value="recomendacoes" className="p-0 m-0">
                  <div className="p-3 sm:p-6 lg:p-8">
                    {(() => {
                      // Log para diagnóstico
                      console.log('Renderizando aba de recomendações:', {
                        temGrupos: Array.isArray(quotation.recommendationGroups) && quotation.recommendationGroups.length > 0,
                        numGrupos: Array.isArray(quotation.recommendationGroups) ? quotation.recommendationGroups.length : 0,
                        temItems: !!(quotation.recommendations && quotation.recommendations.length > 0),
                        numItems: quotation.recommendations?.length || 0,
                        itemIdsNosGrupos: Array.isArray(quotation.recommendationGroups) ? quotation.recommendationGroups.flatMap(g => g.itemIds || []).length : 0
                      });
                      
                      return (Array.isArray(quotation.recommendationGroups) && quotation.recommendationGroups.length > 0 && 
                              quotation.recommendations && quotation.recommendations.length > 0) ? (
                        <div className="space-y-8">
                          {quotation.recommendationGroups.map((group) => {
                            // Filtrar itens associados a este grupo
                            const groupItems = (group.itemIds || [])
                              .map(id => quotation.recommendations?.find(item => item.id === id))
                              .filter(Boolean);
                              
                            console.log(`Grupo "${group.title}": ${groupItems.length} itens encontrados de ${group.itemIds?.length || 0} IDs`);
                            
                            if (groupItems.length === 0) return null;
                            
                            // Check if the group is collapsed, defaulting to true if not found
                            const isCollapsed = collapsedRecommendationGroups[group.id] !== false;
                            
                            return (
                              <div key={group.id} className="border rounded-lg overflow-hidden shadow-sm">
                                <div 
                                  className="bg-gray-50 px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                                  onClick={() => toggleRecommendationGroup(group.id)}
                                >
                                  <h2 className="text-xl sm:text-2xl font-medium text-voah-teal">
                                    {group.title}
                                  </h2>
                                  <div className="flex items-center">
                                    <span className="text-sm text-gray-500 mr-2">
                                      {groupItems.length} {groupItems.length === 1 ? 'item' : 'itens'}
                                    </span>
                                    <button className="text-gray-500 hover:text-gray-700 focus:outline-none">
                                      {isCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
                                    </button>
                                  </div>
                                </div>
                                
                                <div className={`transition-all duration-300 ${
                                  isCollapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'max-h-[5000px] opacity-100'
                                }`}>
                                  <div className="p-4 pt-2 bg-white">
                                    {/* Renderizar a descrição do grupo se existir */}
                                    {group.description && (
                                      <div className="prose prose-sm mb-6 text-gray-700 max-w-none bg-gray-50 p-4 rounded-lg border border-gray-100">
                                        <div dangerouslySetInnerHTML={{ __html: group.description }} />
                                      </div>
                                    )}
                                    
                                    {/* Agrupar itens por tipo */}
                                    {(() => {
                                      const tours = groupItems.filter(item => item?.type === 'tour');
                                      const restaurants = groupItems.filter(item => item?.type === 'restaurant');
                                      const accommodations = groupItems.filter(item => item?.type === 'accommodation');
                                      
                                      return (
                                        <div className="space-y-6">
                                          {tours.length > 0 && (
                                            <div>
                                              <h3 className="flex items-center text-md font-medium text-gray-700 mb-3">
                                                <Map className="h-5 w-5 mr-2 text-voah-teal" />
                                                Passeios ({tours.length})
                                              </h3>
                                              <div className="space-y-4">
                                                {tours.map(item => (
                                                  <RecommendationItem key={item?.id} item={item} />
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          
                                          {restaurants.length > 0 && (
                                            <div>
                                              <h3 className="flex items-center text-md font-medium text-gray-700 mb-3">
                                                <Utensils className="h-5 w-5 mr-2 text-voah-teal" />
                                                Restaurantes ({restaurants.length})
                                              </h3>
                                              <div className="space-y-4">
                                                {restaurants.map(item => (
                                                  <RecommendationItem key={item?.id} item={item} />
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          
                                          {accommodations.length > 0 && (
                                            <div>
                                              <h3 className="flex items-center text-md font-medium text-gray-700 mb-3">
                                                <Building className="h-5 w-5 mr-2 text-voah-teal" />
                                                Hospedagens ({accommodations.length})
                                              </h3>
                                              <div className="space-y-4">
                                                {accommodations.map(item => (
                                                  <RecommendationItem key={item?.id} item={item} />
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="bg-gray-100 p-4 rounded-md text-center text-gray-500">
                          Não há recomendações disponíveis para esta cotação.
                        </div>
                      );
                    })()}
                  </div>
                </TabsContent>
              )}
            </div>
          </Tabs>
        </div>
      </div>
      
      {/* Modal de configurações */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-hidden flex flex-col transition-all duration-300 ease-in-out">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-voah-teal" />
              Configurações de Visualização
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col space-y-5 py-4 overflow-y-auto pr-2">
            {/* Navegação */}
            <div className="border-b pb-4">
              <h3 className="border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left text-base font-medium text-gray-700 mb-3">
                <Settings className="h-4 w-4 text-voah-coral flex-shrink-0" />
                <span className="whitespace-nowrap">Navegação</span>
              </h3>
              
              <div className="flex items-center mb-3">
                <label className="text-sm font-medium w-[30%] pr-4">Modo de Exibição</label>
                <div className="flex items-center gap-2 w-[70%]">
                  <Switch 
                    id="page-day-mode"
                    checked={!!tempConfig?.useDayMode} 
                    onCheckedChange={(checked) => updateTempConfig('useDayMode', checked)}
                  />
                  <Label htmlFor="page-day-mode" className="text-sm font-medium">
                    {tempConfig?.useDayMode ? "Modo Dias" : "Modo Páginas"}
                  </Label>
                  <span className="text-xs text-gray-500 ml-3">
                    {tempConfig?.useDayMode 
                      ? "As páginas são exibidas como dias do roteiro." 
                      : "As páginas são exibidas como seções do documento."}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center mb-3">
                <label className="text-sm font-medium w-[30%] pr-4">Navegação de Páginas</label>
                <div className="flex items-center gap-2 w-[70%]">
                  <Switch 
                    id="use-page-dropdown"
                    checked={!!tempConfig?.usePageDropdown} 
                    onCheckedChange={(checked) => updateTempConfig('usePageDropdown', checked)}
                  />
                  <Label htmlFor="use-page-dropdown" className="text-sm font-medium">
                    {tempConfig?.usePageDropdown ? "Dropdown de páginas" : "Tabs de páginas"}
                  </Label>
                  <span className="text-xs text-gray-500 ml-3">
                    {tempConfig?.usePageDropdown 
                      ? "Recomendado para muitos dias/páginas." 
                      : "Ideal para poucos dias/páginas."}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Mapas */}
            <div className="border-b pb-4">
              <h3 className="border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left text-base font-medium text-gray-700 mb-3">
                <Map className="h-4 w-4 text-voah-coral flex-shrink-0" />
                <span className="whitespace-nowrap">Mapas</span>
              </h3>
              
              <div className="flex items-center mb-3">
                <label className="text-sm font-medium w-[30%] pr-4">Opções do Mapa</label>
                <div className="flex items-center gap-2 w-[70%]">
                  <Switch 
                    id="show-map-lines"
                    checked={tempConfig?.showMapLines !== false} 
                    onCheckedChange={(checked) => updateTempConfig('showMapLines', checked)}
                  />
                  <Label htmlFor="show-map-lines" className="text-sm font-medium">
                    Mostrar linhas no mapa
                  </Label>
                  <span className="text-xs text-gray-500 ml-3">
                    Exibe linhas conectando os pontos no roteiro.
                  </span>
                </div>
              </div>
            </div>
            
            {/* Hospedagens */}
            <div className="border-b pb-4">
              <h3 className="border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left text-base font-medium text-gray-700 mb-3">
                <Building className="h-4 w-4 text-voah-coral flex-shrink-0" />
                <span className="whitespace-nowrap">Hospedagens</span>
              </h3>
              
              <div className="flex items-center mb-3">
                <label className="text-sm font-medium w-[30%] pr-4">Opções de Hospedagem</label>
                <div className="flex items-center gap-2 w-[70%]">
                  <Switch 
                    id="show-accommodations-in-itinerary"
                    checked={!!tempConfig?.showAccommodationsInItinerary} 
                    onCheckedChange={(checked) => updateTempConfig('showAccommodationsInItinerary', checked)}
                  />
                  <Label htmlFor="show-accommodations-in-itinerary" className="text-sm font-medium">
                    Exibir hospedagens no roteiro diário
                  </Label>
                  <span className="text-xs text-gray-500 ml-3">
                    Mostra informações de hospedagem junto com os outros eventos no roteiro diário.
                  </span>
                </div>
              </div>
            </div>
            
            {/* Visibilidade e Nomes das Abas */}
            <div className="border-b pb-4">
              <h3 className="border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left text-base font-medium text-gray-700 mb-3">
                <Layers className="h-4 w-4 text-voah-coral flex-shrink-0" />
                <span className="whitespace-nowrap">Visibilidade e Nomes das Abas</span>
              </h3>
              
              {/* Aba de Hospedagens */}
              <div className="flex items-center mb-3">
                <label className="text-sm font-medium w-[30%] pr-4 text-left">Aba de Hospedagens</label>
                <div className="flex flex-wrap items-center gap-4 w-[70%]">
                  <div className="flex items-center gap-2">
                    <Switch 
                      id="show-accommodations-tab"
                      checked={tempConfig?.showAccommodationsTab !== false} 
                      onCheckedChange={(checked) => updateTempConfig('showAccommodationsTab', checked)}
                    />
                    <Label htmlFor="show-accommodations-tab" className="text-sm font-medium">
                      Mostrar aba
                    </Label>
                  </div>
                </div>
              </div>
              
              {/* Nome da aba de Hospedagens */}
              {tempConfig?.showAccommodationsTab !== false && (
                <div className="flex items-center mb-3 pl-[30%]">
                  <div className="flex flex-col w-full">
                    <Label htmlFor="accommodations-tab-name" className="text-sm font-medium mb-1">
                      Nome da aba
                    </Label>
                    <Input
                      id="accommodations-tab-name"
                      placeholder="Hospedagens"
                      value={tempConfig?.accommodationsTabName || ''}
                      onChange={(e) => updateTempConfig('accommodationsTabName', e.target.value)}
                      className="max-w-xs"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Deixe em branco para usar o nome padrão "Hospedagens".
                    </p>
                  </div>
                </div>
              )}
              
              {/* Aba de Voos */}
              <div className="flex items-center mb-3">
                <label className="text-sm font-medium w-[30%] pr-4 text-left">Aba de Voos</label>
                <div className="flex flex-wrap items-center gap-4 w-[70%]">
                  <div className="flex items-center gap-2">
                    <Switch 
                      id="show-flights-tab"
                      checked={tempConfig?.showFlightsTab !== false} 
                      onCheckedChange={(checked) => updateTempConfig('showFlightsTab', checked)}
                    />
                    <Label htmlFor="show-flights-tab" className="text-sm font-medium">
                      Mostrar aba
                    </Label>
                  </div>
                </div>
              </div>
              
              {/* Nome da aba de Voos */}
              {tempConfig?.showFlightsTab !== false && (
                <div className="flex items-center mb-3 pl-[30%]">
                  <div className="flex flex-col w-full">
                    <Label htmlFor="flights-tab-name" className="text-sm font-medium mb-1">
                      Nome da aba
                    </Label>
                    <Input
                      id="flights-tab-name"
                      placeholder="Voos"
                      value={tempConfig?.flightsTabName || ''}
                      onChange={(e) => updateTempConfig('flightsTabName', e.target.value)}
                      className="max-w-xs"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Deixe em branco para usar o nome padrão "Voos".
                    </p>
                  </div>
                </div>
              )}
              
              {/* Aba de Recomendações */}
              <div className="flex items-center mb-3">
                <label className="text-sm font-medium w-[30%] pr-4 text-left">Aba de Recomendações</label>
                <div className="flex flex-wrap items-center gap-4 w-[70%]">
                  <div className="flex items-center gap-2">
                    <Switch 
                      id="show-recommendations-tab"
                      checked={!!tempConfig?.showRecommendationsTab} 
                      onCheckedChange={(checked) => updateTempConfig('showRecommendationsTab', checked)}
                    />
                    <Label htmlFor="show-recommendations-tab" className="text-sm font-medium">
                      Mostrar aba
                    </Label>
                  </div>
                </div>
              </div>
              
              {/* Nome da aba de Recomendações */}
              {tempConfig?.showRecommendationsTab && (
                <div className="flex items-center mb-3 pl-[30%]">
                  <div className="flex flex-col w-full">
                    <Label htmlFor="recommendations-tab-name" className="text-sm font-medium mb-1">
                      Nome da aba
                    </Label>
                    <Input
                      id="recommendations-tab-name"
                      placeholder="Mais Dicas"
                      value={tempConfig?.recommendationsTabName || ''}
                      onChange={(e) => updateTempConfig('recommendationsTabName', e.target.value)}
                      className="max-w-xs"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Deixe em branco para usar o nome padrão "Mais Dicas".
                    </p>
                  </div>
                </div>
              )}
              
              {/* Aba de Preços */}
              <div className="flex items-center mb-3">
                <label className="text-sm font-medium w-[30%] pr-4 text-left">Aba de Preços</label>
                <div className="flex flex-wrap items-center gap-4 w-[70%]">
                  <div className="flex items-center gap-2">
                    <Switch 
                      id="show-price-tab"
                      checked={!!tempConfig?.showPriceTab} 
                      onCheckedChange={(checked) => updateTempConfig('showPriceTab', checked)}
                    />
                    <Label htmlFor="show-price-tab" className="text-sm font-medium">
                      Mostrar aba
                    </Label>
                  </div>
                </div>
              </div>
              
              {/* Nome da aba de Preços */}
              {tempConfig?.showPriceTab && (
                <div className="flex items-center mb-3 pl-[30%]">
                  <div className="flex flex-col w-full">
                    <Label htmlFor="price-tab-name" className="text-sm font-medium mb-1">
                      Nome da aba
                    </Label>
                    <Input
                      id="price-tab-name"
                      placeholder="Preços"
                      value={tempConfig?.priceTabName || ''}
                      onChange={(e) => updateTempConfig('priceTabName', e.target.value)}
                      className="max-w-xs"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Deixe em branco para usar o nome padrão "Preços".
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Datas - apenas quando em modo dia */}
            {tempConfig?.useDayMode && (
              <div className="border-b pb-4">
                <h3 className="border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left text-base font-medium text-gray-700 mb-3">
                  <Calendar className="h-4 w-4 text-voah-coral flex-shrink-0" />
                  <span className="whitespace-nowrap">Datas</span>
                </h3>
                
                <div className="flex items-center mb-3">
                  <label className="text-sm font-medium w-[30%] pr-4">Datas do Roteiro</label>
                  <div className="flex items-center gap-2 w-[70%]">
                    <Switch 
                      id="use-defined-dates"
                      checked={!!tempConfig?.startDate}
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          updateTempConfig('startDate', null);
                        } else {
                          updateTempConfig('startDate', new Date());
                        }
                      }}
                    />
                    <Label htmlFor="use-defined-dates" className="text-sm font-medium">
                      Usar data definida
                    </Label>
                    <span className="text-xs text-gray-500 ml-3">
                      Define data de início para calcular os dias do roteiro.
                    </span>
                  </div>
                </div>
                
                {tempConfig?.startDate && (
                  <div className="flex items-center pl-[30%]">
                    <div className="flex items-center gap-2 mt-3">
                      <input
                        type="date"
                        id="start-date"
                        value={formatSafeDate(tempConfig.startDate, 'yyyy-MM-dd')}
                        onChange={(e) => {
                          const date = new Date(e.target.value);
                          updateTempConfig('startDate', date);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-voah-teal focus:border-transparent"
                      />
                      <Label htmlFor="start-date" className="text-sm whitespace-nowrap">
                        Data de início
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end mt-6 pt-2 border-t">
            <Button 
              variant="secondary" 
              onClick={() => setShowConfigModal(false)} 
              className="mr-2"
            >
              Cancelar
            </Button>
            <Button 
              onClick={applyConfigChanges}
              className="bg-voah-teal hover:bg-voah-teal-dark"
            >
              Salvar Alterações
            </Button>
          </div>
          
          <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Fechar</span>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </div>
  );
} 

// Componente para cada item de recomendação
function RecommendationItem({ item }: { item: any }) {
  const [showDetails, setShowDetails] = useState(false);
  
  // Se o item não está definido corretamente, renderiza um placeholder
  if (!item || !item.type || !item[item.type]) {
    return (
      <div className="border rounded-lg p-3 bg-gray-50 text-gray-500">
        Item indisponível ou com dados inválidos
      </div>
    );
  }
  
  let itemName = '';
  let itemDescription = '';
  let itemImage = '';
  let itemLocation = '';
  let itemType = '';
  let ItemIcon = MapPin;
  
  // Extrair dados com base no tipo de item
  if (item.type === 'tour' && item.tour) {
    itemName = item.tour.name || '';
    itemDescription = item.tour.description || '';
    itemImage = item.tour.images && item.tour.images.length > 0 ? item.tour.images[0] : '';
    itemLocation = item.tour.location || '';
    itemType = 'Passeio';
    ItemIcon = Map;
  } else if (item.type === 'restaurant' && item.restaurant) {
    itemName = item.restaurant.name || '';
    itemDescription = item.restaurant.description || '';
    itemImage = item.restaurant.images && item.restaurant.images.length > 0 ? item.restaurant.images[0] : '';
    itemLocation = item.restaurant.location || '';
    itemType = 'Restaurante';
    ItemIcon = Utensils;
  } else if (item.type === 'accommodation' && item.accommodation) {
    itemName = item.accommodation.name || '';
    itemDescription = item.accommodation.description || '';
    itemImage = item.accommodation.images && item.accommodation.images.length > 0 ? item.accommodation.images[0] : '';
    itemLocation = item.accommodation.location || '';
    itemType = 'Hospedagem';
    ItemIcon = Building;
  }
  
  // Obter a URL do Google Maps
  const googleMapsUrl = createGoogleMapsUrl(item.type === 'tour' ? item.tour : 
                                          item.type === 'restaurant' ? item.restaurant :
                                          item.type === 'accommodation' ? item.accommodation : null);
  
  return (
    <div className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row bg-white">
      {itemImage && (
        <div className="h-24 md:h-auto md:w-1/4 lg:w-1/5 overflow-hidden relative">
          <img 
            src={itemImage} 
            alt={itemName} 
            className="w-full h-full object-cover"
          />
        </div>
      )}
      
      <div className="p-3 flex-1">
        <div className="flex justify-between items-center mb-1">
          <h3 className="text-base font-medium text-gray-800">{itemName || 'Item sem nome'}</h3>
          
          {/* Botão para mostrar mais detalhes na mesma linha do título */}
          {(itemDescription || itemLocation) && (
            <button 
              onClick={() => setShowDetails(!showDetails)}
              className="text-xs text-voah-teal hover:text-voah-teal-dark underline focus:outline-none ml-2"
            >
              {showDetails ? 'Ocultar' : '+ Detalhes'}
            </button>
          )}
        </div>
        
        <div className="flex flex-col">
          {/* Removido exibição da localização com pin */}
          
          {googleMapsUrl && (
            <a 
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-xs text-voah-teal hover:text-voah-teal-dark"
            >
              <Map className="h-3 w-3 mr-1" />
              Ver no Google Maps
            </a>
          )}
        </div>
        
        {/* Detalhes adicionais - exibidos apenas quando showDetails é true */}
        {showDetails && (
          <div className="mt-2 pt-1 border-t border-gray-100">
            {itemDescription && (
              <div className="text-xs text-gray-700 text-left">
                <div dangerouslySetInnerHTML={{ __html: itemDescription }} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Função para autenticar o acesso à cotação
const checkAuth = async (event: AuthChangeEvent, session: Session | null) => {
  // ... código existente
};

// Função utilitária para formatar datas yyyy-mm-dd para dd/mm/yyyy
function formatDateBR(dateStr?: string) {
  if (!dateStr) return '';
  const [date] = dateStr.split('T');
  const [yyyy, mm, dd] = date.split('-');
  if (yyyy && mm && dd) return `${dd}/${mm}/${yyyy}`;
  return dateStr;
}

